import EventEmitter from 'eventemitter2';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import throttle from 'lodash/throttle';
import uniq from 'lodash/uniq';
import TurndownService from 'turndown';

// eslint-disable-next-line import/no-cycle
import { getScrollingManager } from '../../mobile-library/contentFrameInternals/getScrollingManager';
import type { AnchorScrollTarget, BaseDocument, FirstClassDocument, Highlight, LayoutMode } from '../../types';
import { PointerLikeEvent } from '../../types/browserEvents';
import type { ChunkSanitizationOptions, WebviewChunks } from '../../types/chunkedDocuments';
import type { HighlightResizeState } from '../../types/highlights';
import type { LenientWindow } from '../../types/LenientWindow';
import type { DocumentScrollTarget } from '../../types/misc';
import { isElement, isHTMLElement, notEmpty } from '../../typeValidators';
import cloneDeep from '../../utils/cloneDeep';
import _convertHtmlToText from '../../utils/convertHtmlToText';
import delay from '../../utils/delay';
import {
  isAndroid,
  isDevOrTest,
  isDocumentShareApp,
  isExtension,
  isInReactNativeWebView,
  isIOS,
  isMobile,
} from '../../utils/environment';
import getWords from '../../utils/getWords';
import { convertLexpositionToSerializedPosition } from '../../utils/lexposition';
import { normalizeUrl } from '../../utils/urls';
import { MINIMUM_WORD_COUNT_TO_AUTO_HIGHLIGHT } from '../../wordCountThresholds';
import foregroundEventEmitter from '../eventEmitter';
// eslint-disable-next-line import/no-cycle
import { mobileDestroyContent, mobileInitDocument } from '../mobileContentFramePortalGateMethods';
// eslint-disable-next-line import/no-cycle
import { portalGate as portalGateToForeground } from '../portalGates/contentFrame/from/reactNativeWebview';
import type { HighlightElement } from '../types';
import type { ContentFrameSelectionInfo, InitialScrollPositionData } from '../types/contentFramePuppeteerRelated';
import { ChunkContainerEvent } from '../types/events';
import {
  EnlargementInProgressKnownHighlight,
  KnownHighlight,
  KnownHighlightsMap,
  KnownHighlightStatus,
  KnownHighlightThatFailedToDeserialize,
  KnownHighlightThatFailedToEnlarge,
  RemovalInProgressKnownHighlight,
  RenderedKnownHighlight,
  RenderInProgressKnownHighlight,
} from '../types/knownHighlights';
import type { RangyClassApplier, RangyRange, RangySelection } from '../types/rangy';
import areNodesInDocumentOrder from '../utils/areNodesInDocumentOrder';
import caretRangeFromPoint from '../utils/caretRangeFromPoint';
import checkIsCalibreBlockLevelElement from '../utils/checkIsCalibreBlockLevelElement';
import classListSafe from '../utils/classListSafe';
import cleanUpHtmlForHighlighting from '../utils/cleanUpHtmlForHighlighting';
import clearSelectionUtil from '../utils/clearSelection';
import closestWith from '../utils/closestWith';
import computeHighlightOffset from '../utils/computeHighlightOffset';
import convertRangeToRangyRange from '../utils/convertRangeToRangyRange';
import cropRange from '../utils/cropRange';
import { getIsCurrentlyUsingStylus, initiateStylusTouchDetection } from '../utils/detectIfIsCurrentlyUsingStylus';
import { detectStylusDoubleTap } from '../utils/detectStylusDoubleTap';
import { findContentElements } from '../utils/findAllContentElements';
import { findAllFocusableElements } from '../utils/findAllFocusableElements';
import findText from '../utils/findText';
import getClosestHTMLElement from '../utils/getClosestHTMLElement';
import getCoordinatesObjectFromPointerLikeEvent from '../utils/getCoordinatesObjectFromPointerLikeEvent';
import getElementsBetween from '../utils/getElementsBetween';
import getFirstRangyRangeFromSelectionIfNotEmpty from '../utils/getFirstRangyRangeFromSelectionIfNotEmpty';
import getHighlightableContentRange from '../utils/getHighlightableContentRange';
import getHighlightElements from '../utils/getHighlightElements';
import getHighlightsInRange from '../utils/getHighlightsInRange';
import getHtmlFromRange from '../utils/getHtmlFromRange';
import getOppositeEnd from '../utils/getOppositeEnd';
import getRangeFromNodes from '../utils/getRangeFromNodes';
import getSiblings from '../utils/getSiblings';
import getTextNodeFromHighlightElement from '../utils/getTextNodeFromHighlightElement';
import getVisibleContentElements from '../utils/getVisibleContentElements';
import isElementInRange from '../utils/isElementInRange';
import isFocusableElement from '../utils/isFocusableElement';
import isImage from '../utils/isImage';
import isTextHighlightElement from '../utils/isTextHighlightElement';
import { deserializeCanonicalPosition, deserializeCanonicalRange, serializeRangeAsCanonical } from '../utils/locationSerialization/chunked';
import { makeWebviewLogger } from '../utils/makeWebviewLogger';
import mergeRects from '../utils/mergeRects';
import rangy from '../utils/rangy';
import type { HighlightResult } from '../utils/Renderer';
import Renderer from '../utils/Renderer';
import SelectionEventsHandler from '../utils/SelectionEventsHandler';
import setSelectionBaseAndExtentIfDifferent from '../utils/setSelectionBaseAndExtentIfDifferent';
import trimSelection, { grabPunctuationAtEnd, grabPunctuationAtStart } from '../utils/trimSelection';
import waitForElementsToBeRemovedFromDom from '../utils/waitForElementsToBeRemovedFromDom';
import { contentIsChunked, destroyChunkedContent, forceChunkContentLoadAtPosition, initChunkedContent } from './chunkedContent';
import eventEmitter from './eventEmitter';
// eslint-disable-next-line import/no-cycle
import { clearCssHighlightClass, highlightRange } from './findInDocument';
import {
  forceChunkContentLoadsForHighlight,
  getHighlightsToRenderOnChunkLoad,
  getHighlightsToUnrenderOnChunkUnload,
  initHighlightTrackingForChunking,
  isHighlightFullyInLoadedChunks,
  removeTrackedHighlightAfterDelete,
  trackHighlightForChunking,
} from './highlightsAndChunking';
import {
  checkMutationsRemoveHighlightElements,
  getRenderedHighlightIdsMissingFromDom,
} from './highlightSelfHeal';
// eslint-disable-next-line import/no-cycle
import { type ImmediateDragTextSelection, initializeImmediateDragTextSelectionHandler } from './ImmediateDragTextSelection';
// eslint-disable-next-line import/no-cycle
import * as selectionEmulation from './selectionEmulation';
import hasEnoughBackgroundColorContrast from './utils/hasEnoughBackgroundColorContrast';
import scrollIntoView from './utils/scrollIntoView';

const logger = makeWebviewLogger(__filename);

declare let window: LenientWindow;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let isStaff = false; // This is set in `init`

/*
  What is progressive highlight rendering?

  TL;DR: We sometimes "virtualize" the insertion of highlight elements in the DOM in this file.

  For huge documents (e.g. books) with a lot of highlights, the highlighter in general can be very slow.
  Even just opening the document on a powerful machine can cause everything to freeze, block interactions,
  stop text from rendering on scroll, and so on. Plus it affects other interactions like creating new
  highlights.

  However, deserializing a highlight's serialized location string is not expensive. So when we a call to
  `addHighlight` happens, we don't immediately draw it. Instead, we store the details for later and also
  use IntersectionObserver to track when related "destination" elements are near the viewport, so we
  can render them in time.

  Spreading the workload out over time like this massively improves performance. Right now, we don't unrender
  highlight elements when you scroll them out of view. We'd see more de/serialization issues if we did it
  that often.

  Note: Anything that causes a scroll to a highlight (e.g. clicking a highlight in the notebook panel in the
  sidebar) needs to first trigger the highlight to render if it isn't already.

  Progressive highlight rendering is only used if the document's word count is over a certain threshold. It's
  also never used in the extension because we've no idea what the document is like, if it's overflowing
  horizontally instead of vertically, maybe there are multiple scrolling ancestors, and so on.

  Make sure to check out reading-clients/reader/shared/types/knownHighlights.ts.
*/
let shouldProgressivelyRender = false;

let knownHighlights: KnownHighlightsMap = {};
let isSelfHealingHighlights = false;
const docIdToIdsOfHighlightThatMustExistMap: {
  [docId: string]: Highlight['id'][];
} = {};
let docId: BaseDocument['id'] | undefined;
let documentUrl: string | null = null;
let hasScrollTargetRendered = false;
const highlightElementsSelector = Renderer.getHighlightElementSelector();
let targetToScrollTo: DocumentScrollTarget | null;

let idOfActivatedHighlight: Highlight['id'] | null = null;
let idOfHighlightHoveredOver: Highlight['id'] | null = null;

const initialHighlightResizeState: HighlightResizeState = {
  edgeResizeStartedFrom: null,
  highlightBeingResizedHasAlternativeColor: null,
  idOfHighlightBeingResized: null,
  idsOfHighlightsWithResizeHandlesShown: [],
  status: 'inactive',
};
// You can set this to true to test / work on this in the web app
const shouldEmulateNativeSelectionBehaviourWhenResizingHighlights = isMobile;

/*
  This is controlled from outside (by calling functions defined here). Don't update it inside the content
  frame.
*/
let highlightResizeState: HighlightResizeState = initialHighlightResizeState;
const internalContentFrameEventEmitter = new EventEmitter();
let isInitialized = false;
let lastRightClickedImage: HTMLImageElement | null = null;
let lastRightClickedSelectionInfo: ContentFrameSelectionInfo | null | undefined;
let lastSeenHighlightElements: HighlightElement[] = [];
let mutationObserver: MutationObserver | null = null;
let resizeObserver: ResizeObserver | null = null;
let renderer: Renderer | undefined;
let selectionEventsHandler: SelectionEventsHandler | null = null;
let immediateDragTextSelection: ImmediateDragTextSelection | null = null;
let initialSelectionDetailsOfHighlightBeingResized: Pick<Selection, 'focusNode' | 'focusOffset'> | null =
  null;
let lastSelectionRangeSetByUsWhileResizing: Range | null = null;

/*
  For Quick Lookup:
  On Android, the `contextmenu` event fires on initial long-press (before the user releases their finger)
  with only ~1 word selected. We need to wait until the finger is released so the user can drag to extend
  their selection. This flag defers handling Quick Lookup-applicable selections from the `contextmenu` handler
  until finger release.

  Side note: `onTouchEnd` doesn't fire inside the webview for some reason.

  Set to `true` in the `contextmenu` handler when Quick Lookup is enabled and the selection is "too short".
  Cleared and acted upon by `mobileOnAndroidTouchEnd` (called from React Native's `onTouchEnd` on the
  webview wrapper `View`), which re-evaluates the current selection and emits the Quick Lookup event if it's still
  "too short".

  The valid-selection branch of `contextmenu` also clears this flag (the user selected enough words for
  auto-highlighting to kick in if it's enabled / over the Quick Lookup word limit, so Quick Lookup no longer applies).
*/
let isAndroidQuickLookupPendingFingerRelease = false;
let quickLookupSelectionState: {
  highlightIdsInSelection: Highlight['id'][];
  selectionInfo: Awaited<ReturnType<typeof getCurrentSelectionInfo>>;
  surroundingText?: string;
} | null = null;

// An array of elements from the document text content that may be focused on, like paragraphs, anchors etc
let focusableElements: Element[] = [];

let lastPointerDownEvent: PointerLikeEvent | null = null;
let lastPointerOrTouchMoveEventDuringResize: PointerLikeEvent | null = null;
let removeRenderedScrollTargetListeners: (() => void) | null = null;

let destroyStylusTouchDetection = () => {};
let destroyStylusDoubleTapListeners = () => {};

let copyEventHandler: ((e: ClipboardEvent) => void) | null = null;

const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  emDelimiter: '*',
});

/* Internal functions */

type HighlightToAdd = Pick<Highlight, 'content' | 'html' | 'id' | 'location' | 'source_specific_data'>;

const addHighlight = async (
  { content, html, id, location, source_specific_data: sourceSpecificData }: HighlightToAdd,
  shouldRenderImmediately?: boolean,
): Promise<void> => {
  try {
    if (!isInitialized) {
      throw new Error('Not initialized');
    }

    if (!docId) {
      throw new Error('docId is not set');
    }

    if (knownHighlights[id]) {
      throw new Error('Highlight cannot be added, it already exists in knownHighlights');
    }

    const rendererThatExists = getRendererOrThrow();
    if (!rendererThatExists.classApplier) {
      throw new Error("Renderer's class applier is missing");
    }

    let status: KnownHighlightStatus.NonRendered | KnownHighlightStatus.UnrenderedInUnloadedChunk =
      KnownHighlightStatus.NonRendered;
    const isChunkedContent = contentIsChunked();
    if (isChunkedContent) {
      trackHighlightForChunking(id, location);
      if (!isHighlightFullyInLoadedChunks(id)) {
        status = KnownHighlightStatus.UnrenderedInUnloadedChunk;
      }
    }

    const newKnownHighlight: KnownHighlight = {
      content,
      html,
      id,
      intersectionObserver: null,
      isDrmTruncated: sourceSpecificData?.drm_truncated,
      location,
      renderedData: null,
      status,
    };

    const mustHighlightExist = docIdToIdsOfHighlightThatMustExistMap[docId]?.includes(id);
    if (status === KnownHighlightStatus.UnrenderedInUnloadedChunk && !mustHighlightExist) {
      // skip rendering, we'll automatically render this highlight once all of its chunks are loaded.
      setKnownHighlight(newKnownHighlight);
      return;
    }

    const mustRenderImmediately =
      Boolean(shouldRenderImmediately) ||
      !shouldProgressivelyRender ||
      mustHighlightExist;

    if (!mustRenderImmediately) {
      const onFindFailed = () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        setKnownHighlight({ // Explanation: spreading newKnownHighlight and overriding status to DeserializationFailed produces a valid KnownHighlightThatFailedToDeserialize
          ...newKnownHighlight,
          status: KnownHighlightStatus.DeserializationFailed,
        } as KnownHighlightThatFailedToDeserialize);
        throw new Error('Failed to render');
      };

      let deserializationError: Error | undefined;
      let range: RangyRange | undefined;
      try {
        range = deserializeCanonicalRange(
          location.split('|')[0], // only take first range if there are multiple
          rendererThatExists.containerNode,
          rendererThatExists.containerNode.ownerDocument,
          rendererThatExists.classApplier,
        );
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        deserializationError = e as Error; // Explanation: catch clause variable is unknown; deserializeCanonicalRange throws Error objects
      }

      // Handle error / no result
      if (!range) {
        // eslint-disable-next-line no-console
        const warn = window.location.pathname.startsWith('/reader/shared/') ? console.warn : logger.warn.bind(logger);
        let logMessage = 'Highlight failed to deserialize';
        if (content) {
          logMessage += ', falling back to text search';
        } else {
          logMessage += ". Can't fall back to text search as no text given";
        }
        warn(logMessage, { error: deserializationError, newKnownHighlight });

        // No text, skip fallback text search and exit
        if (!content) {
          onFindFailed();
          return;
        }

        // Fall back to text search.
        const findRange = await findText({
          containerNode: rendererThatExists.containerNode,
          logPrefix: `Highlight#${id}: `,
          text: content,
        });
        if (!findRange) {
          warn("Can't find text", { newKnownHighlight });
          onFindFailed();
          return;
        }
        range = findRange;
      }

      const onIntersect: IntersectionObserverCallback = async (entries, observer) => {
        if (!entries.some(({ isIntersecting }) => isIntersecting)) {
          return;
        }

        // None of the following should happen but I'd prefer to be safe and flag it in dev at least
        if (!isInitialized) {
          logger.warn(
            'IntersectionObserver fired even though content frame is not initialized. Disconnecting...',
          );
          observer.disconnect();
          return;
        }
        const knownHighlight = knownHighlights[id];
        if (!knownHighlight) {
          logger.warn('IntersectionObserver fired for unknown highlight. Disconnecting...', {
            id,
            knownHighlights,
          });
          observer.disconnect();
          return;
        }
        if (knownHighlight.status !== KnownHighlightStatus.NonRendered) {
          logger.warn(
            `IntersectionObserver fired for highlight with '${knownHighlight.status}' status. Disconnecting...`,
            {
              knownHighlight,
            },
          );
          removeIntersectionObserverFromKnownHighlight(knownHighlight.id);
          return;
        }

        if (entries.find((entry) => entry.isIntersecting)) {
          await renderHighlight({
            id,
          });
        }
      };

      const intersectionObserverOptions: IntersectionObserverInit = {
        // `root` is the viewport
        rootMargin: '5000px 0px',
        threshold: 0,
      };
      const scrollAncestor = document.getElementById('document-reader-root');
      if (scrollAncestor) {
        intersectionObserverOptions.root = scrollAncestor;
      }
      const intersectionObserver = new IntersectionObserver(onIntersect, intersectionObserverOptions);

      let startElement = getElementAtEdgeOfRange({
        edge: 'start',
        range,
      });
      let endElement = getElementAtEdgeOfRange({
        edge: 'end',
        range,
      });

      const elements: Element[] = [startElement];

      if (!endElement.isEqualNode(startElement)) {
        const container = getClosestHTMLElement(range.commonAncestorContainer);
        if (!container) {
          throw new Error("Can't get closest HTML element from range common ancestor");
        }

        // Swap them if they're backwards
        if (!areNodesInDocumentOrder(startElement, endElement)) {
          const oldEndElement = endElement;
          endElement = startElement;
          startElement = oldEndElement;
        }

        elements.push(
          ...getElementsBetween({
            container,
            end: endElement,
            start: startElement,
          }),
          endElement,
        );
      }

      for (const element of elements) {
        intersectionObserver.observe(element);
      }

      // eslint-disable-next-line require-atomic-updates
      newKnownHighlight.intersectionObserver = intersectionObserver;
    }

    setKnownHighlight(newKnownHighlight);

    if (mustRenderImmediately) {
      await renderHighlight({
        id,
      });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`Failed to add highlight`, e);
    throw e;
  }
};

const addHighlightIdsToDocIdToIdsOfHighlightThatMustExistMap = (
  docId: BaseDocument['id'],
  highlightIds: Highlight['id'][],
) => {
  if (!highlightIds) {
    return;
  }

  if (!docIdToIdsOfHighlightThatMustExistMap[docId]) {
    docIdToIdsOfHighlightThatMustExistMap[docId] = [];
  }

  for (const highlightId of highlightIds) {
    if (!docIdToIdsOfHighlightThatMustExistMap[docId].includes(highlightId)) {
      docIdToIdsOfHighlightThatMustExistMap[docId].push(highlightId);
    }
  }
};

const renderHighlight = async ({
  id,
}: {
  id: Highlight['id'];
}) => {
  try {
    if (!isInitialized) {
      throw new Error('Not initialized');
    }

    if (!knownHighlights[id]) {
      // Highlight was removed (e.g. during async chunk processing). This is a valid race condition, not a bug.
      return;
    }

    if (
      ![KnownHighlightStatus.EnlargementInProgress, KnownHighlightStatus.NonRendered, KnownHighlightStatus.UnrenderedInUnloadedChunk].includes(
        knownHighlights[id].status,
      )
    ) {
      logger.warn(
        `renderHighlight exiting; the highlight status is '${knownHighlights[id].status}'`,
        knownHighlights[id],
      );
      return;
    }

    if (knownHighlights[id].status === KnownHighlightStatus.UnrenderedInUnloadedChunk) {
      await forceChunkContentLoadsForHighlight(id);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    setKnownHighlight({ // Explanation: spreading knownHighlights[id] and overriding status to RenderInProgress produces a valid RenderInProgressKnownHighlight
      ...knownHighlights[id],
      status: KnownHighlightStatus.RenderInProgress,
    } as RenderInProgressKnownHighlight);
    removeIntersectionObserverFromKnownHighlight(id);

    const rendererThatExists = getRendererOrThrow();
    let highlightResult: HighlightResult | undefined;

    try {
      highlightResult = await rendererThatExists.highlightLocation(
        id,
        knownHighlights[id].location,
        knownHighlights[id].content,
        knownHighlights[id].isDrmTruncated,
      );
    } catch (e) {
      let logMessage = 'Failed to highlight by location';
      if (knownHighlights[id].content) {
        logMessage += ', falling back to text search';
      } else {
        logMessage += ". Can't fall back to text search as no text given";
      }
      logger.debug(logMessage, {
        error: e,
        location: knownHighlights[id].location,
        text: knownHighlights[id].content,
      });

      if (knownHighlights[id].content) {
        highlightResult = await rendererThatExists.highlightText(
          id,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          knownHighlights[id].content as string, // Explanation: guarded by if (knownHighlights[id].content) above ensuring content is a non-empty string
        );
        if (!highlightResult) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          setKnownHighlight({ // Explanation: spreading knownHighlights[id] and overriding status to DeserializationFailed produces a valid KnownHighlightThatFailedToDeserialize
            ...knownHighlights[id],
            status: KnownHighlightStatus.DeserializationFailed,
          } as KnownHighlightThatFailedToDeserialize);

          throw new Error('Failed to highlight by location and text search');
        }
      } else {
        throw e;
      }
    }

    if (!highlightResult.elements.length) {
      logger.warn('No elements highlighted, may already be highlighted');
    }

    const renderedData: RenderedKnownHighlight['renderedData'] = {
      content: highlightResult.rangeText,
      html: highlightResult.html,
      rangyHighlight: highlightResult.rangyHighlight,
    };

    if (isDevOrTest) {
      const cleanRenderedContent = renderedData.content?.trim();
      const cleanKnownHighlightContent = knownHighlights[id].content?.trim();

      const cleanRenderedHtml = cleanUpHtmlForHighlighting(renderedData.html);
      const cleanKnownHighlightHtml = cleanUpHtmlForHighlighting(knownHighlights[id].html);

      const doesContentDiffer = cleanRenderedContent !== cleanKnownHighlightContent;
      const doesHtmlDiffer = cleanRenderedHtml !== cleanKnownHighlightHtml;

      if (doesContentDiffer || doesHtmlDiffer) {
        // DRM-truncated highlights intentionally store less content than what's rendered from the DOM.
        // Skip the warning if the highlight is DRM-truncated and stored content is a prefix of rendered content.
        const doesDrmTruncationExplainDifference = knownHighlights[id].isDrmTruncated &&
          cleanKnownHighlightContent &&
          cleanRenderedContent?.startsWith(cleanKnownHighlightContent);

        if (!doesDrmTruncationExplainDifference) {
          logger.warn('New rendered highlight data differs from saved / expected data', {
            cleanKnownHighlightContent,
            cleanKnownHighlightHtml,
            cleanRenderedContent,
            cleanRenderedHtml,
            knownHighlight: knownHighlights[id],
            renderedData,
          });
        }
      }
    }

    setKnownHighlight({
      ...knownHighlights[id],
      id,
      renderedData,
      status: KnownHighlightStatus.Rendered,
    } as RenderedKnownHighlight);

    const isActivated = idOfActivatedHighlight === id;
    const resizeStatus =
      highlightResizeState.idOfHighlightBeingResized === id ? highlightResizeState.status : undefined;

    for (const highlightElement of highlightResult.elements) {
      if (isActivated) {
        classListSafe.add(highlightElement, getActiveClassName(highlightElement));
      }
      if (resizeStatus) {
        highlightElement.dataset.resizeStatus = resizeStatus;
      }
      if (isImage(highlightElement)) {
        highlightElement.addEventListener('click', onClickImageHighlight);
      }
    }

    if (
      id === highlightResizeState.idOfHighlightBeingResized &&
      highlightResizeState.status === 'user-interaction-done-waiting-for-render'
    ) {
      await onHighlightResizeComplete(id);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to render highlight', e);
    await resetHighlightResizeStateIfWaitingForRender(id);
    let didResizeHandleVisibilityChange = false;
    if (id === idOfActivatedHighlight) {
      idOfActivatedHighlight = null;
      didResizeHandleVisibilityChange = true;
    }
    if (id === idOfHighlightHoveredOver) {
      idOfHighlightHoveredOver = null;
      didResizeHandleVisibilityChange = true;
    }
    if (didResizeHandleVisibilityChange) {
      onHighlightResizeHandlesVisibilityUpdated();
    }
    throw e;
  }
};

const destroyObservers = () => {
  mutationObserver?.disconnect();
  mutationObserver = null;
  resizeObserver?.disconnect();
  resizeObserver = null;
};

const getActiveClassName = (highlightElement: HighlightElement): string => {
  const baseClassName = isImage(highlightElement)
    ? Renderer.imageHighlightClassName
    : Renderer.textHighlightTagName;
  return `${baseClassName}--active`;
};

function getElementAtEdgeOfRange({
  edge,
  range,
}: {
  edge: 'end' | 'start';
  range: RangyRange;
}): HTMLElement {
  const rendererThatExists = getRendererOrThrow();
  const edgeContainer = range[`${edge}Container`];

  if (isHTMLElement(edgeContainer)) {
    return edgeContainer;
  }

  const closestHTMLElement = getClosestHTMLElement(edgeContainer);
  if (closestHTMLElement && !closestHTMLElement.isEqualNode(rendererThatExists.containerNode)) {
    return closestHTMLElement;
  }

  const elementSiblings = getSiblings({
    direction: edge === 'end' ? 'next' : 'previous',
    element: edgeContainer,
    matcher: isHTMLElement,
    shouldIncludeNonElements: true,
  });
  if (elementSiblings.siblings.length) {
    const firstSibling = elementSiblings.siblings[0];
    if (isHTMLElement(firstSibling)) {
      return firstSibling;
    }
  }

  return rendererThatExists.containerNode;
}

const getHighlightElementsForId = (highlightId?: Highlight['id']): HighlightElement[] => {
  if (!isInitialized || !highlightId) {
    return [];
  }
  return getHighlightElements({
    container: getRendererOrThrow().containerNode,
    id: highlightId,
  });
};

const getHighlightElementsInDom = (container?: HTMLElement): HighlightElement[] => {
  if (!container) {
    return [];
  }

  return getHighlightElements({ container });
};

const getDoc = () => getRendererOrThrow().containerNode.ownerDocument;

const getHighlightIdsInRange = (range?: Range): Highlight['id'][] => {
  if (!range || range.collapsed) {
    return [];
  }

  return getHighlightsInRange(
    Object.keys(knownHighlights).map((id) => ({
      id,
      elements: getHighlightElementsForId(id),
    })),
    range,
  ).map(({ id }) => id);
};

const getHighlightIdsInRangeDescribedBySerializedLocation = async (
  location: string,
): Promise<Highlight['id'][]> => {
  if (!renderer) {
    throw new Error('Renderer is missing');
  }
  if (!renderer.classApplier) {
    throw new Error("Renderer's class applier is missing");
  }

  let range: RangyRange | undefined;
  try {
    range = deserializeCanonicalRange(
      location.split('|')[0], // only take first range if there are multiple
      renderer.containerNode,
      renderer.containerNode.ownerDocument,
      renderer.classApplier,
    );
  } catch (e) {
    logger.warn('Failed to deserialize highlight range', {
      error: e,
      location,
      renderer,
    });
  }

  if (!range || range.collapsed) {
    return [];
  }

  return getHighlightIdsInRange(range);
};

const getSel = (): ReturnType<typeof rangy.getSelection> => rangy.getSelection(getWindow());

async function getSelectionInfoFromSelection(
  selection: RangySelection | undefined,
  options: Omit<Parameters<typeof getSelectionInfoFromRange>[0], 'range'> & {
    shouldNotGrabPunctuation?: boolean;
    shouldClearSelection?: boolean;
    shouldIgnoreHighlightResizeStateWhenTrimming?: boolean;
  },
): Promise<ContentFrameSelectionInfo | undefined> {
  if (!selection || !selection.rangeCount || selection.isCollapsed) {
    return;
  }

  const rangeTextBefore = getFirstRangyRangeFromSelectionIfNotEmpty(selection)?.toString();

  const resultOfTrim = trimSelection({
    getHighlightResizeState: () =>
      options.shouldIgnoreHighlightResizeStateWhenTrimming
        ? {
          edgeResizeStartedFrom: null,
          highlightBeingResizedHasAlternativeColor: null,
          idOfHighlightBeingResized: null,
          idsOfHighlightsWithResizeHandlesShown: [],
          status: 'inactive',
        }
        : highlightResizeState,
    selection,
    shouldNotGrabPunctuation: !isInReactNativeWebView || options.shouldNotGrabPunctuation,
  });

  // Log if it should've been trimmed but wasn't
  if (isDevOrTest) {
    if (resultOfTrim.start.reasonWhyItDidNotTrim !== 'not allowed to modify') {
      if (
        rangeTextBefore?.trimStart() !== rangeTextBefore &&
        rangeTextBefore === getFirstRangyRangeFromSelectionIfNotEmpty(selection)?.toString()
      ) {
        logger.warn('trimSelection did not trim start', {
          range: getFirstRangyRangeFromSelectionIfNotEmpty(selection),
          options,
          rangeTextBefore,
          resultOfTrim,
        });
      }
    } else if (resultOfTrim.end.reasonWhyItDidNotTrim !== 'not allowed to modify') {
      if (
        rangeTextBefore?.trimEnd() !== rangeTextBefore &&
        rangeTextBefore === getFirstRangyRangeFromSelectionIfNotEmpty(selection)?.toString()
      ) {
        logger.warn('trimSelection did not trim end', {
          range: getFirstRangyRangeFromSelectionIfNotEmpty(selection),
          options,
          rangeTextBefore,
          resultOfTrim,
        });
      }
    }
  }

  if (!selection?.rangeCount) {
    return;
  }

  const range: RangyRange | null = selection.getRangeAt(0);
  if (!range || range.collapsed) {
    return;
  }

  const rendererThatExists = getRendererOrThrow();

  let expandedRange: RangyRange | null = range;

  if (options.shouldExpandToHighlightBounds) {
    for (const highlightId of getHighlightIdsInRange(range)) {
      expandedRange = expandedRange.union(getRangeFromNodes(getHighlightElementsForId(highlightId)));
    }
  }

  if (rendererThatExists.containerNode !== document.body) {
    expandedRange = cropRange(expandedRange, getHighlightableContentRange(rendererThatExists.containerNode));
  }

  if (!expandedRange || expandedRange.collapsed) {
    return;
  }

  const result = await getSelectionInfoFromRange({
    range: expandedRange,
    ...options,
  });

  if (options.shouldClearSelection) {
    clearSelectionUtil();
  }

  return result;
}

const getSelectionInfoFromRange = ({
  range,
  shouldExpandToHighlightBounds,
}: {
  range: Range;
  shouldExpandToHighlightBounds?: boolean;
}): ContentFrameSelectionInfo => {
  const rangyRange = convertRangeToRangyRange(range);
  const rendererThatExists = getRendererOrThrow();

  let expandedRange = rangyRange;
  if (shouldExpandToHighlightBounds) {
    const highlightIdsInRange = getHighlightIdsInRange(rangyRange);
    const highlightElementsInRange: HighlightElement[] = [];

    for (const highlightId of highlightIdsInRange) {
      const elementsInRange = getHighlightElementsForId(highlightId).filter((element) =>
        isElementInRange(element, rangyRange));
      if (elementsInRange.length) {
        highlightElementsInRange.push(...elementsInRange);
      }
    }

    expandedRange = highlightElementsInRange.length
      ? rangyRange.union(getRangeFromNodes(highlightElementsInRange))
      : rangyRange;
  }

  const html = cleanUpHtmlForHighlighting(getHtmlFromRange(expandedRange));

  const offset = computeHighlightOffset(expandedRange, rendererThatExists);

  const result = {
    html,
    isHighlightableRangeResult: rendererThatExists.isHighlightableRange(expandedRange, {}),
    location: serializeRangeAsCanonical({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      classApplier: rendererThatExists.classApplier as RangyClassApplier, // Explanation: classApplier on an initialized renderer is always a RangyClassApplier; the type is broader for optional initialization state
      containerNode: rendererThatExists.containerNode,
      range: expandedRange,
    }),
    markdown: convertHtmlToText(html, documentUrl || ''),
    offset,
    text: expandedRange.toString().replace(/\u2060/g, ''), // Remove Word-Joiner
  };

  return result;
};

/*
  Walks from a set of starting DOM elements up to their nearest content-block
  ancestors, then extracts and truncates the surrounding text
*/
function getSurroundingTextFromElements(startElements: Iterable<HTMLElement>): string | undefined {
  const rendererThatExists = getRendererOrThrow();
  const ancestors = new Set<HTMLElement>();
  for (const element of startElements) {
    const ancestor = closestWith<HTMLElement>(
      element,
      (node) =>
        isContentElement(node, rendererThatExists.containerNode) && isHTMLElement(node),
    );
    if (ancestor) {
      ancestors.add(ancestor);
    }
  }

  const maxBlocks = 4;
  const maxChars = 1500;
  const minChars = Math.floor(maxChars * 0.8);

  const blocks: string[] = [];
  for (const element of ancestors) {
    const text = convertHtmlToText(element.innerHTML, documentUrl || '')?.trim();
    if (text) {
      blocks.push(text);
    }
    if (blocks.length >= maxBlocks) {
      break;
    }
  }

  if (!blocks.length) {
    return undefined;
  }

  const combined = blocks.join('\n\n');
  if (combined.length <= maxChars) {
    return combined;
  }

  let truncated = combined.slice(0, maxChars);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  if (lastSpaceIndex > minChars) {
    truncated = truncated.slice(0, lastSpaceIndex);
  }
  return `${truncated}…`;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const getWindow = () => getDoc().defaultView as Window; // Explanation: defaultView returns Window | null but is always non-null in the browser context this code runs in

const handleHighlightResizePointerOrTouchMove = throttle(
  function handleHighlightResizePointerOrTouchMoveUnthrottled(event: PointerEvent | TouchEvent) {
    logger.debug('handleHighlightResizePointerOrTouchMove: start', {
      event,
      highlightResizeState,
      lastPointerOrTouchMoveEventDuringResize,
      shouldEmulateNativeSelectionBehaviourWhenResizingHighlights,
    });
    if (
      highlightResizeState.status === 'inactive' ||
      highlightResizeState.status === 'user-interaction-done-waiting-for-render' ||
      !shouldEmulateNativeSelectionBehaviourWhenResizingHighlights
    ) {
      return;
    }

    const previousPointerOrTouchMoveEventDuringResize = lastPointerOrTouchMoveEventDuringResize;
    lastPointerOrTouchMoveEventDuringResize = event;

    const coordinates = getCoordinatesObjectFromPointerLikeEvent(event);
    if (!coordinates) {
      return;
    }

    /*
      This fixes an issue found on Boox using a stylus (I can't say with absolute certainty that it never
      happened elsewhere). Let's say you have two paragraphs like:

      > Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam gravida ultrices massa, sed
      rutrum lacus mattis eget. Donec pulvinar dapibus dolor nec fringilla.

      > Morbi molestie accumsan sapien, sit amet gravida dolor finibus sitamet. Fusce rhoncus, ex in...

      Imagine you highlighted the first paragraph, then you started resizing the end handle and move it
      towards so it was just above a character in "sitamet". Right on the top boundary of the line,
      i.e. the top of the client rect. It make take a few tries to reproduce this but anyway, the
      selection will jump from ending at the end of the first paragraph and somewhere in "sitamet", even
      if you think you're not moving the stylus.

      I found that event's coordinates are different by a small fraction. Ignoring differences that small
      (also factoring in the throttling) fixes it for me.
    */
    if (previousPointerOrTouchMoveEventDuringResize) {
      const previousCoordinates = getCoordinatesObjectFromPointerLikeEvent(
        previousPointerOrTouchMoveEventDuringResize,
      );
      if (
        Math.abs(coordinates.clientX - previousCoordinates.clientX) < 1 &&
        Math.abs(coordinates.clientY - previousCoordinates.clientY) < 1
      ) {
        return;
      }
    }

    let clientY = coordinates.clientY;

    // https://linear.app/readwise/issue/RW-38371/resizing-cursor-position-is-different-from-native-text-selection
    if (event instanceof TouchEvent && !getIsCurrentlyUsingStylus()) {
      const touchRadiusY = event.touches[0].radiusY;
      const adjustment = -touchRadiusY - (isAndroid ? 32 : 16);
      clientY = Math.max(coordinates.clientY + adjustment, 0);
    }

    const caretRange = caretRangeFromPoint(coordinates.clientX, clientY);
    const selection = getSelection();
    if (!caretRange || !selection) {
      return;
    }
    const highlightElement = document.querySelector(
      Renderer.getHighlightElementSelector(highlightResizeState.idOfHighlightBeingResized),
    );
    if (!highlightElement) {
      return;
    }

    const highlightElementRange = getRangeFromNodes([highlightElement]);
    const endResizeDidNotStartFrom = getOppositeEnd(highlightResizeState.edgeResizeStartedFrom ?? 'end');
    const newSelectionArgs: Parameters<typeof selection.setBaseAndExtent> = [
      highlightElementRange[`${endResizeDidNotStartFrom}Container`],
      highlightElementRange[`${endResizeDidNotStartFrom}Offset`],
      caretRange.startContainer,
      caretRange.startOffset,
    ];

    logger.debug('handleHighlightResizePointerOrTouchMove: setting selection base and extent', {
      event,
      highlightResizeState,
      lastPointerOrTouchMoveEventDuringResize,
      newSelectionArgs,
      shouldEmulateNativeSelectionBehaviourWhenResizingHighlights,
    });
    selection.setBaseAndExtent(...newSelectionArgs);

    lastSelectionRangeSetByUsWhileResizing = selection.getRangeAt(0).cloneRange();
  },
  100,
  { leading: true },
);

/*
  Re-render a highlight whose <rw-highlight> element was removed from the DOM by the page itself.
  Remove + re-add mirrors a deactivate/reactivate cycle: it clears the stale Rangy state (the DOM
  nodes are gone but the content frame still tracks them) before rendering fresh marks.
*/
const reRenderHighlightRemovedFromDom = async (id: Highlight['id']): Promise<void> => {
  const knownHighlight = knownHighlights[id];
  if (!knownHighlight) {
    return;
  }

  const { content, html, isDrmTruncated, location } = knownHighlight;
  await removeHighlights([id]);

  if (!isInitialized) {
    return;
  }

  await addHighlight(
    { content, html, id, location, source_specific_data: { drm_truncated: isDrmTruncated } },
    true,
  );
};

/*
  Extension only:
  Sites that hydrate the whole document with React (hydrateRoot(document, …)) reconcile <body> and
  strip our injected highlight marks. The content frame tracks rendered state in memory, so it never
  notices and never re-adds them. Debounced so it runs once after the hydration churn settles.
*/
const selfHealHighlightsRemovedFromDom = debounce(async (containerNode: HTMLElement) => {
  if (!isInitialized || !isExtension || !containerNode.isConnected || isSelfHealingHighlights) {
    return;
  }

  const missingHighlightIds = getRenderedHighlightIdsMissingFromDom({ containerNode, knownHighlights });
  if (!missingHighlightIds.length) {
    return;
  }

  isSelfHealingHighlights = true;
  logger.warn('Re-rendering highlights removed from the DOM by the page', {
    missingHighlights: missingHighlightIds.map((id) => knownHighlights[id]),
  });

  try {
    for (const id of missingHighlightIds) {
      if (!isInitialized) {
        break;
      }
      await reRenderHighlightRemovedFromDom(id);
    }
  } catch (error) {
    logger.error('Failed to re-render highlights removed from the DOM', { error });
  } finally {
    // eslint-disable-next-line require-atomic-updates
    isSelfHealingHighlights = false;
  }
}, 250);

const initializeObservers = ({
  containerNode,
}: {
  containerNode: HTMLElement;
}) => {

  /*
    Run it once in case there are existing highlights to be safe.
    This handles when targetToOpenAt changes (to a highlight ID).
  */
  processHighlights({ contentContainer: containerNode });

  // Listen for new highlight elements
  mutationObserver = new MutationObserver(async (mutationsList) => {
    if (!isInitialized) {
      logger.warn(
        'contentFrame MutationObserver fired when content frame is not initialized. Disconnecting...',
      );
      mutationObserver?.disconnect();
      mutationObserver = null;
      return;
    }

    /*
      The page removing our highlights (e.g. React hydrating the whole document) doesn't match the filter
      below (the removed node is often an ancestor of the highlights) so detect it here.
    */
    if (isExtension && checkMutationsRemoveHighlightElements(mutationsList, highlightElementsSelector)) {
      selfHealHighlightsRemovedFromDom(containerNode);
    }

    if (
      !mutationsList.some(
        ({ target }) =>
          isHTMLElement(target) &&
          (target.isEqualNode(containerNode) ||
            isTextHighlightElement(target) ||
            target.matches(highlightElementsSelector)),
      )
    ) {
      return;
    }

    // When deleting an image highlight in the extenion, we need a little timeout
    if (
      isExtension &&
      mutationsList.length === 1 &&
      mutationsList[0].attributeName === 'class' &&
      isImage(mutationsList[0].target)
    ) {
      setTimeout(() => {
        if (!mutationObserver || !isInitialized) {
          return;
        }
        processHighlights({ contentContainer: containerNode });
      }, 10);
      return;
    }

    processHighlights({ contentContainer: containerNode });
  });

  mutationObserver.observe(containerNode, {
    attributes: true,
    attributeFilter: ['class'],
    childList: true,
    subtree: true,
  });

  // Emit an event when images load
  resizeObserver = new ResizeObserver(() => {
    portalGateToForeground.emit('content-moved');
  });
  for (const img of containerNode.querySelectorAll('img')) {
    resizeObserver.observe(img);
  }
};

function isContentElement(node: Node, containerNode: Element): boolean {
  if (!(node instanceof HTMLElement) || node.isEqualNode(containerNode)) {
    return node.isEqualNode(containerNode);
  }

  const contentElements = findContentElements(containerNode, { shouldExcludeMedia: true });
  return contentElements.includes(node);
}

function isHighlightResizeWaitingForRender(highlightId: Highlight['id'] | 'any'): boolean {
  return (
    highlightResizeState.status === 'user-interaction-done-waiting-for-render' &&
    ['any', highlightResizeState.idOfHighlightBeingResized].includes(highlightId)
  );
}

// This modifies the selection if needed, i.e. it'll move the start/end
function makeSureSelectionDoesntStartOrEndInHighlightResizeHandle(selection: RangySelection) {
  const range = getFirstRangyRangeFromSelectionIfNotEmpty(selection);
  if (!range) {
    return;
  }

  const rangyRange = convertRangeToRangyRange(range);
  let didUpdateRange = false;

  for (const item of ['end', 'start']) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const end = item as 'end' | 'start'; // Explanation: iterating over a literal array ['end', 'start']; item is always one of those two strings
    const containerElement = getClosestHTMLElement(rangyRange[`${end}Container`]);
    if (!containerElement) {
      continue;
    }
    const handleElement = containerElement.closest(`.${Renderer.highlightResizeHandleClassName}`);
    if (handleElement) {
      const highlightElement = handleElement.closest<HighlightElement>(
        Renderer.getHighlightElementSelector(),
      );
      if (!highlightElement) {
        continue;
      }
      const highlightTextNode = getTextNodeFromHighlightElement(highlightElement);
      if (!highlightTextNode) {
        continue;
      }
      if (end === 'end') {
        rangyRange.setEnd(highlightTextNode, highlightTextNode.length);
      } else {
        rangyRange.setStart(highlightTextNode, 0);
      }
      didUpdateRange = true;
    }
  }

  if (didUpdateRange) {
    selection.setSingleRange(rangyRange);
  }
}

const onClickImageHighlight: EventListenerOrEventListenerObject = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const onContextMenuOpened = async (event: MouseEvent) => {
  logger.debug('onContextMenuOpened', { event });

  if (isInReactNativeWebView) {
    if (
      event.target instanceof Node &&
        getClosestHTMLElement(event.target)?.closest(`.${Renderer.highlightResizeHandleClassName}`) ||
      highlightResizeState.status !== 'inactive'
    ) {
      return;
    }

    if (window.isAutoHighlightingEnabled || window.isQuickLookupUsable || !isAndroid) {
      event.preventDefault();
    }
    if (isAndroid) {
      const isHighlightableSelectionResult = renderer?.isHighlightableSelection({
        highlightResizeState,
      });
      if (isHighlightableSelectionResult?.isValid) {
        isAndroidQuickLookupPendingFingerRelease = false;
        const selectionInfoExpandedToHighlightBounds = await getCurrentSelectionInfo({
          shouldExpandToHighlightBounds: true,
          shouldNotGrabPunctuation: false,
        });

        portalGateToForeground.emit('valid-selection-completed', {
          finalEventName: 'contextmenu',
          selectionInfoExpandedToHighlightBounds,
        });
        portalGateToForeground.emit('valid-manual-selection-completed', {
          isTooShortToAutoHighlight: false,
          selectionInfoExpandedToHighlightBounds: selectionInfoExpandedToHighlightBounds ?? undefined,
        });
      } else if (isHighlightableSelectionResult?.reason === 'too-short') {
        // See comment above `isAndroidQuickLookupPendingFingerRelease` definition to understand
        if (window.isQuickLookupUsable) {
          isAndroidQuickLookupPendingFingerRelease = true;
          return;
        }

        const selectionInfoExpandedToHighlightBounds = await getCurrentSelectionInfo({
          shouldExpandToHighlightBounds: true,
          shouldNotGrabPunctuation: true,
        });
        portalGateToForeground.emit('valid-manual-selection-completed', {
          isTooShortToAutoHighlight: true,
          selectionInfoExpandedToHighlightBounds: selectionInfoExpandedToHighlightBounds ?? undefined,
        });
      }
    }
    return;
  }

  lastRightClickedImage = event.target instanceof HTMLImageElement ? event.target : null;
  lastRightClickedSelectionInfo = lastRightClickedImage ? null : await getCurrentSelectionInfo({});
};

const onHighlightElementClicked: EventListenerOrEventListenerObject = (event) => {
  // Unrounded for accuracy; not persisted to RxDB
  // eslint-disable-next-line no-restricted-syntax
  const sourceEventTimestamp = Date.now();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const target = getClosestHTMLElement(event.target as Node); // Explanation: event.target in a DOM event listener is always a Node; EventTarget is the broader interface type
  if (!target || target.closest('a')) {
    return;
  }
  event.stopPropagation();
  event.preventDefault();

  const closestHighlightElement = target.closest<HighlightElement>(Renderer.getHighlightElementSelector());

  if (!closestHighlightElement) {
    throw new Error("Can't find nearest highlight element from clicked element");
  }

  let iconClicked: 'note' | 'tag' | null = null;
  if (target.closest(`.${Renderer.highlightIconWrapperClassName}`)) {
    if (target.closest(`.${Renderer.highlightNoteIconClassName}`)) {
      iconClicked = 'note';
    } else if (target.closest(`.${Renderer.highlightTagIconClassName}`)) {
      iconClicked = 'tag';
    }
  }
  portalGateToForeground.emit('highlight-clicked', {
    highlightElementClassName: closestHighlightElement.className,
    iconClicked,
    id: closestHighlightElement.dataset.highlightId,
    sourceEventTimestamp,
  });
};

function onHighlightElementPointerOut(event: PointerEvent) {
  logger.debug('onHighlightElementPointerOut', { event });
  // Touch pointers don't represent hover — only mouse and pen (e.g. Boox stylus) do.
  if (event.pointerType === 'touch') {
    return;
  }
  const closestHighlightElement = getClosestHTMLElement(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    event.target as Node | null, // Explanation: event.target in a DOM event listener is always a Node or null; EventTarget is the broader interface type
  )?.closest<HighlightElement>(Renderer.getHighlightElementSelector());

  if (!closestHighlightElement) {
    return;
  }

  // To be safe, only remove from elements related to ID if it exists, otherwise all highlight elements
  if (
    !closestHighlightElement.dataset.highlightId ||
    idOfHighlightHoveredOver === closestHighlightElement.dataset.highlightId
  ) {
    idOfHighlightHoveredOver = null;
  }
  onHighlightResizeHandlesVisibilityUpdated();

  let elementsToRemoveClassFrom: HighlightElement[] = [];
  if (closestHighlightElement.dataset.highlightId) {
    elementsToRemoveClassFrom = getHighlightElementsForId(closestHighlightElement.dataset.highlightId);
  } else if (renderer) {
    elementsToRemoveClassFrom = Array.from(
      renderer.containerNode.querySelectorAll<HighlightElement>(`.${Renderer.highlightHoverClassName}`),
    );
  }

  for (const element of elementsToRemoveClassFrom) {
    classListSafe.remove(element, Renderer.highlightHoverClassName);
  }
}

function onHighlightElementPointerOver(event: PointerEvent) {
  logger.debug('onHighlightElementPointerOver', { event });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- listener wired to a 'pointerover' event, which always dispatches a PointerEvent
  const pointerEvent = event as PointerEvent;

  /*
   * Touch pointers don't represent hover. We still want to support hover for mouse and pen (e.g. Boox
   * stylus). Using pointer events with pointerType lets us check the input source directly on the event,
   * rather than relying on a fragile timestamp heuristic against touchstart/touchend.
   */
  if (pointerEvent.pointerType === 'touch') {
    return;
  }
  const closestHighlightElement = getClosestHTMLElement(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    event.target as Node | null, // Explanation: event.target in a DOM event listener is always a Node or null; EventTarget is the broader interface type
  )?.closest<HighlightElement>(Renderer.getHighlightElementSelector());

  if (!closestHighlightElement) {
    return;
  }

  if (!closestHighlightElement.dataset.highlightId) {
    throw new Error('Highlight is missing highlightId data attribute');
  }

  idOfHighlightHoveredOver = closestHighlightElement.dataset.highlightId;
  onHighlightResizeHandlesVisibilityUpdated();

  for (const element of getHighlightElementsForId(closestHighlightElement.dataset.highlightId)) {
    classListSafe.add(element, Renderer.highlightHoverClassName);
  }
}

function onHighlightElementTouchStart() {
  // https://linear.app/readwise/issue/RW-38715/if-you-tap-an-existing-selection-above-a-highlight-do-not-activate-the#comment-a5234eae
  clearSelectionUtil();
}

async function onHighlightResizeComplete(highlightId: Highlight['id']) {
  logger.debug('onHighlightResizeComplete', { highlightId });
  await resetHighlightResizeStateIfWaitingForRender(highlightId);
}

function onHighlightResizeHandleMouseDownOrTouchStart(event: PointerLikeEvent) {
  logger.debug('onHighlightResizeHandleMouseDownOrTouchStart', {
    event,
    shouldEmulateNativeSelectionBehaviourWhenResizingHighlights,
  });

  if (event instanceof MouseEvent && event.buttons !== 1) {
    return;
  }

  if (isMobile || shouldEmulateNativeSelectionBehaviourWhenResizingHighlights) {
    if (event.cancelable) {
      // This prevents scroll on mobile. On web, it prevents the default native selection behaviour
      event.preventDefault();
    }
    if (event instanceof MouseEvent) {
      // Without this, it wouldn't know the event started a selection
      selectionEventsHandler?.notifyOfPreventedMouseDown(event);
    }
  }

  if (!event.target) {
    return;
  }

  const handleElement = getClosestHTMLElement(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    event.target as Node, // Explanation: event.target in a DOM event listener is always a Node; EventTarget is the broader interface type
  )?.closest<HighlightElement>(
    `.${Renderer.highlightResizeHandleClassName}`,
  );
  if (!handleElement) {
    return;
  }

  const highlightElement = handleElement.closest<HighlightElement>(
    Renderer.getHighlightElementSelector(),
  );

  if (!highlightElement?.dataset.highlightId) {
    return;
  }

  setHighlightResizeState({
    ...highlightResizeState,
    edgeResizeStartedFrom: handleElement.classList.contains(
      Renderer.highlightResizeHandleClassNameByEdge.start,
    )
      ? 'start'
      : 'end',
    highlightBeingResizedHasAlternativeColor: highlightElement.classList.contains(
      `${Renderer.highlightClassName}--alternative-color`,
    ),
    idOfHighlightBeingResized: highlightElement.dataset.highlightId,
    status: 'native-selection-made-but-user-hasnt-started-resizing-yet',
  });
}

function onHighlightResizeHandlesVisibilityUpdated() {
  const updatedValue: HighlightResizeState = {
    ...highlightResizeState,
    idsOfHighlightsWithResizeHandlesShown: uniq(
      [idOfActivatedHighlight, idOfHighlightHoveredOver].filter(notEmpty),
    ),
  };
  logger.debug('onHighlightResizeHandlesVisibilityUpdated', {
    oldValue: highlightResizeState,
    updatedValue,
  });
  setHighlightResizeState(updatedValue);
}

const onHighlightElementDoubleClicked: EventListenerOrEventListenerObject = (event) => {
  // Unrounded for accuracy; not persisted to RxDB
  // eslint-disable-next-line no-restricted-syntax
  const sourceEventTimestamp = Date.now();
  event.stopPropagation();
  event.preventDefault();

  const eventTarget = event.target;
  if (!eventTarget || !(eventTarget instanceof Node) || !isHTMLElement(eventTarget)) {
    return;
  }
  const target = eventTarget;
  const highlightElement = target.closest<HighlightElement>(
    [Renderer.textHighlightTagName, `.${Renderer.imageHighlightClassName}`].join(', '),
  );

  if (!highlightElement) {
    throw new Error("Can't find nearest highlight element from clicked element");
  }

  let iconClicked: 'note' | 'tag' | null = null;
  if (target.closest(`.${Renderer.highlightIconWrapperClassName}`)) {
    if (target.closest(`.${Renderer.highlightNoteIconClassName}`)) {
      iconClicked = 'note';
    } else if (target.closest(`.${Renderer.highlightTagIconClassName}`)) {
      iconClicked = 'tag';
    }
  }

  portalGateToForeground.emit('highlight-double-clicked', {
    id: highlightElement.dataset.highlightId,
    iconClicked,
    sourceEventTimestamp,
  });
};

function onPointerCancel(event: PointerEvent) {
  logger.debug('onPointerCancel', { event });
  portalGateToForeground.emit('pointercancel');
  onPointerOrTouchCancel(event);
}

async function onPointerOrTouchCancel(event: PointerEvent | TouchEvent) {
  logger.debug('onPointerOrTouchCancel', {
    ...pick(event, ['eventPhase', 'target', 'type']),
    event,
    highlightResizeState,
  });

  if (highlightResizeState.status === 'inactive') {
    return;
  }

  await resetHighlightResizeState();
  idOfHighlightHoveredOver = null;
  onHighlightResizeHandlesVisibilityUpdated();
}

function onPointerDown(event: PointerEvent) {
  portalGateToForeground.emit('pointerdown');
  lastPointerDownEvent = event;

  if (highlightResizeState.status !== 'actively-resizing') {
    return;
  }

  setHighlightResizeState({
    ...highlightResizeState,
    edgeResizeStartedFrom: null,
    highlightBeingResizedHasAlternativeColor: null,
    idOfHighlightBeingResized: null,
    status: 'inactive',
  });
}

function onPointerOrTouchMove(event: PointerEvent | TouchEvent) {
  if (
    highlightResizeState.status === 'inactive' ||
    !shouldEmulateNativeSelectionBehaviourWhenResizingHighlights
  ) {
    return;
  }

  if (event.cancelable) {
    event.preventDefault();
  }

  handleHighlightResizePointerOrTouchMove(event);
}

async function onPointerUp() {
  if (highlightResizeState.status !== 'native-selection-made-but-user-hasnt-started-resizing-yet') {
    return;
  }
  await resetHighlightResizeState();
  idOfHighlightHoveredOver = null;
  onHighlightResizeHandlesVisibilityUpdated();
}

function onTouchCancel(event: TouchEvent) {
  onPointerOrTouchCancel(event);
  portalGateToForeground.emit('touchcancel');
}

const prepareHighlightElementsForDeletion = (elements: HighlightElement[]): void => {

  /*
    Remove extra attributes for rangy
  */

  // eslint-disable-next-line no-restricted-syntax
  for (const element of elements) {
    const htmlElement: HTMLElement = element;
    htmlElement.removeEventListener('click', onHighlightElementClicked);
    htmlElement.removeEventListener('pointerover', onHighlightElementPointerOver);
    htmlElement.removeEventListener('pointerout', onHighlightElementPointerOut);
    htmlElement.removeEventListener('touchstart', onHighlightElementTouchStart);
    element
      .querySelectorAll<HTMLElement>(`.${Renderer.highlightResizeHandleClassName}`)
      .forEach((handleElement) => {
        handleElement.removeEventListener(
          isMobile ? 'touchstart' : 'mousedown',
          onHighlightResizeHandleMouseDownOrTouchStart,
        );
      });

    // If there are any new attributes on the element, rangy will skip it, so we'll remove them and any extra classes
    if (element.tagName.toLowerCase() === 'rw-highlight') {
      Array.from(element.attributes).forEach(({ nodeName }) => {
        if (nodeName !== 'class') {
          element.removeAttribute(nodeName);
        }
      });
      // eslint-disable-next-line no-param-reassign
      element.className = Renderer.highlightClassName;

      element
        .querySelectorAll(Renderer.getHighlightCustomChildSelector())
        .forEach((descendant) => descendant.remove());
    } else {
      // Image highlight
      element.removeEventListener('click', onClickImageHighlight);
    }

    element.removeAttribute('data-highlight-id');
  }
};

const processHighlights = throttle(
  async ({
    contentContainer,
  }: {
    contentContainer: HTMLElement;
  }) => {
    if (!isInitialized) {
      return;
    }

    const highlightElements = getHighlightElementsInDom(contentContainer);
    for (const highlightElement of highlightElements) {

      // Just for TS. Otherwise these event listeners will get the generic Event type
      const htmlElement: HTMLElement = highlightElement;
      htmlElement.addEventListener('click', onHighlightElementClicked);
      htmlElement.addEventListener('pointerover', onHighlightElementPointerOver);
      htmlElement.addEventListener('pointerout', onHighlightElementPointerOut);
      htmlElement.addEventListener('touchstart', onHighlightElementTouchStart);
      highlightElement
        .querySelectorAll<HTMLElement>(`.${Renderer.highlightResizeHandleClassName}`)
        .forEach((handleElement) => {
          // Can't make this touchstart passive; if passive, the page will still scroll
          handleElement.addEventListener(
            isMobile ? 'touchstart' : 'mousedown',
            onHighlightResizeHandleMouseDownOrTouchStart,
          );
        });
      highlightElement.ondblclick = onHighlightElementDoubleClicked;

      if (
        targetToScrollTo?.category === 'highlightId' &&
        highlightElement.dataset.highlightId === targetToScrollTo.item &&
        !hasScrollTargetRendered
      ) {
        hasScrollTargetRendered = true;

        /*
          Why is there a delay? If there's a text fragment in the URL, the browser
          will scroll to that first.
          - We can't detect if there's a text fragment.
          - We can't prevent the browser from scrolling to the text fragment.
          - The browser could scroll to the wrong occurrence, so we scroll too.
          Worst case scenario: the browser scrolls to one location and then we
          scroll to another, there's a little visual jump.
          Side note: the default CSS highlighting for text fragments is unset.
        */
        await delay(10);

        if (!isInReactNativeWebView) {
          highlightElement.scrollIntoView({
            block: 'center', // vertical
            inline: 'center', // horizontal
          });
        } else {
          // The highlight elements should exist here; we should be safe calling scrollToElement
          getScrollingManager()._scrollToRangeOrElement(highlightElement);
        }
        portalGateToForeground.emit('scroll-target-scrolled-to');
      }

      const alternativeColorClassName = `${Renderer.highlightClassName}--alternative-color`;
      if (

        /*
          This `.contains` shouldn't be needed right? No, `.add` always modifies the element,
          triggering any MutationObserver. So in this case we'd get an infinite loop
        */
        !highlightElement.classList.contains(alternativeColorClassName) &&
        !hasEnoughBackgroundColorContrast(highlightElement)
      ) {
        classListSafe.add(highlightElement, alternativeColorClassName);
      }
    }

    if (!isEqual(highlightElements, lastSeenHighlightElements)) {
      lastSeenHighlightElements = highlightElements;
      portalGateToForeground.emit('highlight-elements-changed');
    }
  },
  50,
  {
    leading: true,
    trailing: true,
  },
);

function removeActiveClassFromAllHighlightElements() {
  const activeHighlightElements = Array.from(
    getRendererOrThrow().containerNode.querySelectorAll<HighlightElement>(
      `.${Renderer.highlightClassName}--active`,
    ),
  );
  for (const highlightElement of activeHighlightElements) {
    classListSafe.remove(highlightElement, getActiveClassName(highlightElement));
  }
}

const removeIntersectionObserverFromKnownHighlight = (id: Highlight['id']): void => {
  if (!knownHighlights[id]) {
    throw new Error('No highlight with that ID');
  }
  knownHighlights[id].intersectionObserver?.disconnect();
  knownHighlights[id].intersectionObserver = null;
};

async function resetHighlightResizeState() {
  logger.debug('resetHighlightResizeState');
  await setHighlightResizeState({
    ...initialHighlightResizeState,
    idsOfHighlightsWithResizeHandlesShown: uniq(
      [idOfActivatedHighlight, idOfHighlightHoveredOver].filter(notEmpty),
    ),
  });
}

async function setHighlightResizeState(value: HighlightResizeState) {
  if (isExtension) {
    return;
  }
  logger.debug('setHighlightResizeState: start', {
    oldValue: highlightResizeState,
    value,
  });
  if (isEqual(value, highlightResizeState)) {
    return;
  }

  const oldValue = cloneDeep(highlightResizeState);
  highlightResizeState = value;
  portalGateToForeground.emit('highlight-resize-state-updated', highlightResizeState);

  if (renderer) {
    if (renderer.containerNode.dataset.rwHighlightResizeStatus !== highlightResizeState.status) {
      renderer.containerNode.dataset.rwHighlightResizeStatus = highlightResizeState.status;
    }

    if (highlightResizeState.highlightBeingResizedHasAlternativeColor) {
      if (renderer.containerNode.dataset.rwHighlightBeingResizedHasAlternativeColor !== 'true') {
        renderer.containerNode.dataset.rwHighlightBeingResizedHasAlternativeColor = 'true';
      }
    } else {
      delete renderer.containerNode.dataset.rwHighlightBeingResizedHasAlternativeColor;
    }

    /*
      This was the only way we found to fix https://linear.app/readwise/issue/RW-38717/highlight-resize-abruptly-ends.
      Note: it didn't work when updating touch-action via CSS / it took too long to be applied.

      A side benefit is that it disables unwanted gestures when the handles are shown.

      Later, we discovered https://linear.app/readwise/issue/RW-38778/specific-highlight-in-epub-crashes-app
      and the only fix we could find was to not update the touch-action on iOS (which wasn't strictly
      necessary to fix the original issue anyway since it was only happening on Android).
    */
    if (!isIOS && !isExtension) {
      if (highlightResizeState.status === 'inactive') {
        if (highlightResizeState.idsOfHighlightsWithResizeHandlesShown.length) {
          document.documentElement.style.touchAction = 'pan-y';
        } else {
          document.documentElement.style.removeProperty('touch-action');
        }
      } else {
        document.documentElement.style.touchAction = 'none';
      }
    }
  }

  if (highlightResizeState.status === 'inactive') {
    initialSelectionDetailsOfHighlightBeingResized = null;
    selectionEmulation.stopEmulatingSelection();

    if (renderer && oldValue.idOfHighlightBeingResized) {
      const oldHighlightElements = getHighlightElementsForId(oldValue.idOfHighlightBeingResized);
      for (const oldHighlightElement of oldHighlightElements) {
        delete oldHighlightElement.dataset.resizeStatus;
      }
    }

    return;
  }

  if (!renderer) {
    return;
  }

  const highlightElements = getHighlightElementsForId(highlightResizeState.idOfHighlightBeingResized);
  for (const highlightElement of highlightElements) {
    if (highlightElement.dataset.resizeStatus !== highlightResizeState.status) {
      highlightElement.dataset.resizeStatus = highlightResizeState.status;
    }
  }

  if (highlightResizeState.status === 'native-selection-made-but-user-hasnt-started-resizing-yet') {
    clearSelectionUtil();
    await delay(5); // Needed before creating selection

    // Has it changed?
    if (highlightResizeState.status !== 'native-selection-made-but-user-hasnt-started-resizing-yet') {
      return;
    }

    // Select the highlight elements (query for them again to be safe)
    const highlightElements = getHighlightElementsForId(highlightResizeState.idOfHighlightBeingResized);
    if (!highlightElements.length) {
      logger.warn(
        'No highlight elements found when highlight resize status changed to native-selection-made-but-user-hasnt-started-resizing-yet status',
      );
      return;
    }
    const range = getRangeFromNodes(highlightElements);
    const selection = rangy.getSelection();
    logger.debug('setHighlightResizeState: setting selection', { highlightResizeState, range });
    selection.setSingleRange(range);
    initialSelectionDetailsOfHighlightBeingResized = pick(selection, ['focusNode', 'focusOffset']);

    if (shouldEmulateNativeSelectionBehaviourWhenResizingHighlights) {
      if (!lastPointerDownEvent) {
        throw new Error("Starting resize but lastPointerDownEvent doesn't exist");
      }
      selectionEmulation.startEmulatingSelection({ lastPointerDownEvent });
    }

    return;
  }

  if (highlightResizeState.status === 'user-interaction-done-waiting-for-render') {
    selectionEmulation.pauseEmulatingSelection();
  }
}

const setKnownHighlight = (knownHighlight: KnownHighlight): void => {
  // Do some validation first
  for (const propertyPath of [
    'content',
    'html',
    'id',
    'location',
    'intersectionObserver',
    'renderedData',
    'status',
  ]) {
    if (typeof get(knownHighlight, propertyPath) === 'undefined') {
      throw new Error(`knownHighlight.${propertyPath} is undefined`);
    }
  }

  if (knownHighlight.status === KnownHighlightStatus.Rendered) {
    for (const propertyPath of ['renderedData.content', 'renderedData.html']) {
      const value = get(knownHighlight, propertyPath);
      if (typeof value === 'undefined') {
        throw new Error(
          `knownHighlight.${propertyPath} is undefined, even though status is '${KnownHighlightStatus.Rendered}'`,
        );
      }
      if (value === null) {
        throw new Error(
          `knownHighlight.${propertyPath} is null, even though status is '${KnownHighlightStatus.Rendered}'`,
        );
      }
    }
  }

  knownHighlights[knownHighlight.id] = knownHighlight;
};


/*
  Exported stuff below this point:
*/

export const addHighlights = async (
  highlights: Highlight[],
  shouldRenderImmediately?: boolean,
): Promise<Highlight['id'][]> => {
  if (!isInitialized) {
    throw new Error('Not initialized');
  }
  logger.debug('addHighlights', { highlights, knownHighlights, shouldRenderImmediately });

  const failedHighlightIds: Highlight['id'][] = [];
  for (const highlight of highlights) {
    // Note: this used to lookup by location only, not ID
    if (knownHighlights[highlight.id]) {
      logger.warn(`Not adding highlight#${highlight.id} as it's already in knownHighlights`);
      continue;
    }

    try {
      if (!isInitialized) {
        return [];
      }
      await addHighlight(highlight, shouldRenderImmediately);
    } catch (e) {
      failedHighlightIds.push(highlight.id);
      logger.error('Failed to add highlight', { highlight, e });
    }
  }

  return failedHighlightIds;
};

export async function clearSelection() {
  clearSelectionUtil();
}

export async function getQuickLookupSelectionState() {
  return quickLookupSelectionState;
}

export async function startQuickLookupSelectionEmulation() {
  const [highlightIdsInSelection, selectionInfo] = await Promise.all([
    getHighlightIdsInSelection({ serializedLocationToUseIfThereIsNoSelection: undefined }),
    getCurrentSelectionInfo({}),
  ]);
  const surroundingText = getSurroundingTextForSelection();
  quickLookupSelectionState = { highlightIdsInSelection, selectionInfo, surroundingText };
  selectionEmulation.startEmulatingSelection({ renderingMode: 'cssHighlights', shouldAutoScroll: false });
  selectionEmulation.pauseEmulatingSelection();
  clearSelectionUtil();
}

export async function stopQuickLookupSelectionEmulation() {
  selectionEmulation.stopEmulatingSelection();
  quickLookupSelectionState = null;
  clearSelectionUtil();
  portalGateToForeground.emit('invalid-selection-completed');
}

// Intercepts copy/cut events in the webview to enforce DRM word limits. This handles keyboard
// shortcuts (e.g., Ctrl+C in Android emulator) which bypass the native copy buttons that use
// expo-clipboard for truncation.
export function setCopyWordLimit(limit: number | null, languageCode?: string): void {
  if (copyEventHandler) {
    document.removeEventListener('copy', copyEventHandler);
    document.removeEventListener('cut', copyEventHandler);
    copyEventHandler = null;
  }

  if (limit === null || limit <= 0) {
    return;
  }

  copyEventHandler = (e: ClipboardEvent) => {
    const selection = window.getSelection()?.toString() || '';
    const words = getWords(selection, languageCode ?? 'unknown');

    if (words.length <= limit) {
      return;
    }

    e.preventDefault();
    const truncated = words.slice(0, limit).join(' ');
    e.clipboardData?.setData(
      'text/plain',
      `${truncated}\n\n[Selection limited to ${limit} words due to restrictions]`,
    );
  };
  document.addEventListener('copy', copyEventHandler);
  document.addEventListener('cut', copyEventHandler);
}

export const convertHtmlToText = _convertHtmlToText;

export const destroy = async (): Promise<void> => {
  if (!isInitialized) {
    logger.warn('destroy called when isInitialized is already false');
    return;
  }
  logger.debug('Destroy: start');

  try {
    const doc = getDoc();

    focusableElements = [];
    documentUrl = null;
    shouldProgressivelyRender = false;
    lastPointerDownEvent = null;
    lastSeenHighlightElements = [];
    idOfActivatedHighlight = null;
    idOfHighlightHoveredOver = null;
    selectionEventsHandler?.destroy();
    selectionEventsHandler = null;
    immediateDragTextSelection?.destroy();
    immediateDragTextSelection = null;
    isAndroidQuickLookupPendingFingerRelease = false;
    quickLookupSelectionState = null;
    destroyStylusTouchDetection();
    destroyStylusDoubleTapListeners();
    setCopyWordLimit(null);
    initialSelectionDetailsOfHighlightBeingResized = null;
    lastSelectionRangeSetByUsWhileResizing = null;
    selectionEmulation.stopEmulatingSelection();

    await removeHighlights('all');

    renderer = undefined;

    doc.removeEventListener('contextmenu', onContextMenuOpened);
    doc.removeEventListener('pointercancel', onPointerCancel);
    doc.removeEventListener('pointerdown', onPointerDown);
    doc.removeEventListener('pointerup', onPointerUp);
    doc.removeEventListener(isMobile ? 'touchmove' : 'pointermove', onPointerOrTouchMove);
    doc.removeEventListener('touchcancel', onTouchCancel);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    doc.ondblclick = () => {};

    // This should be reset by the ContentFramePuppeteer but let's be safe
    setHighlightResizeState(initialHighlightResizeState);

    destroyObservers();
    if (isMobile) {
      await mobileDestroyContent();
    }
    destroyChunkedContent();
    eventEmitter.removeAllListeners();
    // eslint-disable-next-line require-atomic-updates
    isInitialized = false;
    logger.debug('Destroy: end');
  } catch (error) {
    // eslint-disable-next-line require-atomic-updates
    isInitialized = false;
    logger.error('Destroy: error', { error });
    throw error;
  }
};

export const enlargeHighlight = async (highlight: Highlight): Promise<void> => {
  logger.debug('enlargeHighlight', { highlight, knownHighlights });
  if (!knownHighlights[highlight.id]) {
    throw new Error('Unknown highlight ID');
  }
  if (knownHighlights[highlight.id].location === highlight.location) {
    logger.warn("Not enlarging highlight as it's already rendered at that location");
    return;
  }
  if (knownHighlights[highlight.id].status !== KnownHighlightStatus.Rendered) {
    if (knownHighlights[highlight.id].status === KnownHighlightStatus.EnlargementFailed) {
      logger.warn('enlargeHighlight exiting because this knownHighlight previouisly failed to enlarge');
      return;
    }
    throw new Error(`Highlight status is '${knownHighlights[highlight.id].status}'`);
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  setKnownHighlight({ // Explanation: spreading knownHighlights[id] and overriding fields to EnlargementInProgress status produces a valid EnlargementInProgressKnownHighlight
    ...knownHighlights[highlight.id],
    content: highlight.content,
    html: highlight.html,
    isDrmTruncated: highlight.source_specific_data?.drm_truncated,
    location: highlight.location,
    status: KnownHighlightStatus.EnlargementInProgress,
  } as EnlargementInProgressKnownHighlight);
  if (contentIsChunked()) {
    trackHighlightForChunking(highlight.id, highlight.location);
  }

  try {
    await removeHighlights([highlight.id], false);
    await renderHighlight({ id: highlight.id });
  } catch (e) {
    logger.error('enlargeHighlight errored', { error: e });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    setKnownHighlight({ // Explanation: spreading knownHighlights[id] and overriding status to EnlargementFailed produces a valid KnownHighlightThatFailedToEnlarge
      ...knownHighlights[highlight.id],
      status: KnownHighlightStatus.EnlargementFailed,
    } as KnownHighlightThatFailedToEnlarge);
    await resetHighlightResizeStateIfWaitingForRender(highlight.id);
    throw e;
  }
};

export const enlargeHighlights = async (highlights: Highlight[]): Promise<void> => {
  logger.debug('enlargeHighlights', { highlights, knownHighlights });
  for (const highlight of highlights) {
    try {
      await enlargeHighlight(highlight);
    } catch (error) {
      logger.error('Failed to enlarge highlight', { error, highlight });
      continue;
    }
  }
};

export const getSelectionInfoFromElements = async ({
  elements,
  ...options
}: Parameters<typeof getCurrentSelectionInfo>[0] & { elements: HTMLElement[]; }) => {
  const range = getRangeFromNodes(elements);

  if (!range || range.collapsed) {
    return;
  }

  return getSelectionInfoFromRange({
    range,
    ...options,
  });
};

export const getHighlightBoundingClientRect = async (id: Highlight['id']) => {
  const highlightRects = getHighlightElementsForId(id).map((element) => element.getBoundingClientRect());
  return mergeRects(highlightRects);
};

export const getSelectionBoundingClientRect = () => {
  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);

  if (range.collapsed) {
    return null;
  }

  const rects = Array.from(range.getClientRects());

  if (rects.length === 0) {
    return null;
  }

  if (rects.length === 1) {
    return rects[0];
  }

  return mergeRects(rects);
};

export const getFocusableElementIndexForSelection = async () => {
  // Get surrounding paragraph serialization
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const rendererThatExists = renderer as Renderer; // Explanation: renderer is typed as Renderer | undefined; at this call site the caller is expected to have initialized it; treated as always-defined
  if (!rendererThatExists.containerNode) {
    return;
  }
  if (focusableElements.length === 0) {
    focusableElements = findAllFocusableElements(rendererThatExists.containerNode);
  }

  const selection = getSel();
  if (!selection?.rangeCount) {
    return;
  }
  trimSelection({
    getHighlightResizeState: () => highlightResizeState,
    selection,
    shouldNotGrabPunctuation: true,
  });
  if (!selection?.rangeCount) {
    return;
  }

  const range: RangyRange | null = selection.getRangeAt(0);
  if (!range || range.collapsed) {
    return;
  }
  const closestElement = getClosestHTMLElement(selection.focusNode);

  let target: Element | HTMLElement | undefined = closestElement;
  if (
    closestElement &&
    !isFocusableElement(closestElement) &&
    !focusableElements.includes(closestElement)
  ) {
    // try to see if a parent node of this element is focusable
    target = closestWith(
      closestElement,
      (node: Node) =>
        isElement(node) &&
        focusableElements.includes(node) &&
        (isFocusableElement(node) || node === rendererThatExists.containerNode),
    );
  }
  if (target === rendererThatExists.containerNode || !target) {
    // We went too far, lets just assume we have no target
    return;
  }

  return focusableElements.indexOf(target);
};

export const getCurrentSelectionInfo = async (
  options: Parameters<typeof getSelectionInfoFromSelection>[1],
): ReturnType<typeof getSelectionInfoFromSelection> => {
  return getSelectionInfoFromSelection(getSel(), options);
};

export const getSelectionInfoForResizingHighlight = async (
  options: Parameters<typeof getSelectionInfoFromSelection>[1],
): ReturnType<typeof getSelectionInfoFromSelection> => {
  const selection = getSel();
  if (!selection || !selection.rangeCount || selection.isCollapsed) {
    if (lastSelectionRangeSetByUsWhileResizing) {
      selection.setSingleRange(convertRangeToRangyRange(lastSelectionRangeSetByUsWhileResizing));
    } else {
      return;
    }
  }

  makeSureSelectionDoesntStartOrEndInHighlightResizeHandle(selection);
  return getSelectionInfoFromSelection(selection, options);
};

export const getSelectionInfoFromSelector = async ({
  selector,
  ...options
}: Parameters<typeof getCurrentSelectionInfo>[0] & { selector: string; }): ReturnType<
  typeof getCurrentSelectionInfo
> => {
  const rendererThatExists = getRendererOrThrow();
  const elements = Array.from(rendererThatExists.containerNode.querySelectorAll<HTMLElement>(selector));

  if (!elements.length) {
    return;
  }

  return getSelectionInfoFromElements({ elements, ...options });
};

export async function selectMatchingText(text: string) {
  const rendererThatExists = getRendererOrThrow();
  const range = await findText({
    text,
    containerNode: rendererThatExists.containerNode,
  });

  if (!range) {
    return;
  }

  const rangySelection = rangy.getSelection();
  rangySelection.setSingleRange(range);
}

export const getKnownHighlights = async (): Promise<typeof knownHighlights> => knownHighlights;

export const getHighlightElementsInSelection = async (): Promise<HighlightElement[]> => {
  const selection = getSel();
  if (!selection?.rangeCount) {
    return [];
  }
  return getHighlightsInRange(
    Object.keys(knownHighlights).map((id) => ({
      id,
      elements: getHighlightElementsForId(id),
    })),
    selection.getRangeAt(0),
  )
    .map(({ elements }) => elements)
    .flat();
};

export const getHighlightIdsInSelection = async ({
  serializedLocationToUseIfThereIsNoSelection,
}: {
  serializedLocationToUseIfThereIsNoSelection?: string;
}): Promise<Highlight['id'][]> => {
  const selection = getSel();
  const range = selection?.rangeCount ? selection.getRangeAt(0) : null;

  if (!range || range.collapsed) {
    if (serializedLocationToUseIfThereIsNoSelection) {
      return getHighlightIdsInRangeDescribedBySerializedLocation(
        serializedLocationToUseIfThereIsNoSelection,
      );
    }
    return [];
  }

  return getHighlightIdsInRange(range);
};

export const getHighlightIdsInSelector = async ({
  selector,
}: { selector: string; }): Promise<Highlight['id'][]> => {
  const rendererThatExists = getRendererOrThrow();
  const elements = Array.from(rendererThatExists.containerNode.querySelectorAll(selector));

  if (!elements.length) {
    return [];
  }

  const range = getRangeFromNodes(elements);

  if (!range || range.collapsed) {
    return [];
  }

  return getHighlightIdsInRange(range);
};

export const getLastRightClickedImageSelectionInfo = async (): Promise<
  ContentFrameSelectionInfo | undefined
> => {
  if (!lastRightClickedImage) {
    return;
  }
  const range = rangy.createRange();
  range.selectNode(lastRightClickedImage);
  if (range.collapsed) {
    return;
  }
  return getSelectionInfoFromRange({
    range,
  });
};

export const getLastRightClickedSelectionInfo = async (): Promise<
  typeof lastRightClickedSelectionInfo
> => lastRightClickedSelectionInfo;

export function getRendererOrThrow(): Renderer {
  if (!renderer) {
    throw new Error('Renderer does not exist');
  }
  return renderer;
}

async function onChunkContentLoaded(chunkContainerEvent: ChunkContainerEvent) {
  const { chunkId } = chunkContainerEvent;
  const highlightsToRender = getHighlightsToRenderOnChunkLoad(chunkId);
  const highlightsToActuallyRender = highlightsToRender
    .filter((id) => knownHighlights[id]?.status === KnownHighlightStatus.UnrenderedInUnloadedChunk);
  if (highlightsToActuallyRender.length === 0) {
    return;
  }
  logger.debug('rendering highlights on chunk load', { chunkId, highlightsToActuallyRender });
  for (const id of highlightsToActuallyRender) {
    await renderHighlight({ id });
  }
}

function onChunkContentUnloaded(chunkContainerEvent: ChunkContainerEvent) {
  const { chunkId } = chunkContainerEvent;
  const highlightsToUnrender = getHighlightsToUnrenderOnChunkUnload(chunkId);
  const highlightsToActuallyUnrender = highlightsToUnrender
    .filter((id) => knownHighlights[id]?.status === KnownHighlightStatus.Rendered);
  if (highlightsToActuallyUnrender.length === 0) {
    return;
  }
  logger.debug('unrendering highlights on chunk unload', { chunkId, highlightsToActuallyUnrender });
  for (const id of highlightsToActuallyUnrender) {
    const knownHighlight = knownHighlights[id];
    if (!knownHighlight) {
      logger.error('No such highlight to unrender on chunk unload', { id, chunkId, highlightsToActuallyUnrender });
      continue;
    }
    setKnownHighlight({
      ...knownHighlight,
      status: KnownHighlightStatus.UnrenderedInUnloadedChunk,
      renderedData: null,
    });
  }
}

export const init = async ({
  chunks,
  containerNodeSelector = 'body',
  docId: docIdArgument,
  documentUrl: documentUrlArgument,
  querySelector = (selector: string) => document.querySelector(selector),
  sanitizationOptions,
  shouldProgressivelyRender: shouldProgressivelyRenderUrlArgument,
  layoutMode,
  initialPositionData,
}: {
  chunks?: WebviewChunks;
  containerNodeSelector?: string;
  documentUrl?: string;
  docId?: BaseDocument['id'];
  // Only used by tests
  querySelector?: ParentNode['querySelector'];
  sanitizationOptions?: ChunkSanitizationOptions;
  shouldProgressivelyRender: boolean;
  initialPositionData?: InitialScrollPositionData;
  layoutMode: LayoutMode;
}): Promise<void> => {
  const containerNode = querySelector<HTMLElement>(containerNodeSelector);
  documentUrl = documentUrlArgument ?? null;
  docId = docIdArgument;
  logger.debug('Init: start', { docId, containerNodeSelector });
  shouldProgressivelyRender = shouldProgressivelyRenderUrlArgument;

  if (docId) {

    /*
      Why prune it here in init rather than destroy? This is to work around the fact that the content frame
      could be destroyed & re-initialized during the lifetime of one document. So basically, we're only
      removing stale documents from this map once the document ID has changed (when init is called)
    */
    for (const key of Object.keys(docIdToIdsOfHighlightThatMustExistMap)) {
      if (key !== docId) {
        delete docIdToIdsOfHighlightThatMustExistMap[key];
      }
    }
  }

  if (!containerNode) {
    throw new Error('No containerNode');
  }

  // The class is in different places per platform
  isStaff = Boolean(containerNode.closest('.is-staff,.is-not-staff')?.classList.contains('is-staff'));

  if (isInitialized) {
    logger.warn(
      "Content frame init exiting, already initialized (maybe it didn't get destroyed properly?)",
    );
    return;
  }
  renderer = new Renderer({
    canHighlightStartInAnother: isInReactNativeWebView,
    containerNode,
  });

  const doc = containerNode.ownerDocument;

  destroyStylusTouchDetection = initiateStylusTouchDetection();

  focusableElements = findAllFocusableElements(containerNode);

  const isHighlightableSelection: Renderer['isHighlightableSelection'] = (...args) => {
    if (!renderer) {
      logger.warn('renderer does not exist when expected');
      return { isValid: false, reason: 'no-renderer' };
    }

    return renderer.isHighlightableSelection(...args);
  };

  /*
    This function takes a selection and extends it to grab all the punctuation characters
    at the end of the selected text. (In IOS, typically the selection stops before punctuation)
    This only works on iOS (modifying selection on other platforms breaks active selection events)
   */
  const modifyOnGoingSelectionToGrabPunctuationOnIOS = () => {
    if (!isIOS) {
      return;
    }
    const selection = getSel();
    if (!selection?.rangeCount) {
      return;
    }
    grabPunctuationAtStart(selection, () => highlightResizeState);
    grabPunctuationAtEnd(selection, () => highlightResizeState);
  };

  /*
    We select the whole highlight (all elements) when entering
    `native-selection-made-but-user-hasnt-started-resizing-yet` state. Then when the selection is
    updated, we make sure the anchor (node & offset) is kept the same (which is the opposite end of
    the highlight element(s) than the resize handle which is being used).
    This is good in general but there also sometimes in Safari the selection updates erratically.
  */
  function modifySelectionToMaintainHighlightElementEndAsAnchor(selection: Selection) {
    const edgeResizeStartedFrom = highlightResizeState.edgeResizeStartedFrom;
    if (!edgeResizeStartedFrom || selection.isCollapsed) {
      return;
    }

    const highlightElements = getHighlightElementsForId(highlightResizeState.idOfHighlightBeingResized);
    const oppositeEnd = getOppositeEnd(edgeResizeStartedFrom);
    const highlightElementAtOppositeEnd =
      highlightElements[oppositeEnd === 'start' ? 0 : highlightElements.length - 1];
    const highlightElementAtOppositeEndRange = getRangeFromNodes([highlightElementAtOppositeEnd]);

    const focusNode = selection.focusNode ?? selection.anchorNode;
    if (!focusNode) {
      throw new Error('Selection has no focusNode or anchorNode');
    }

    setSelectionBaseAndExtentIfDifferent(
      [
        highlightElementAtOppositeEndRange[`${oppositeEnd}Container`],
        highlightElementAtOppositeEndRange[`${oppositeEnd}Offset`],
        focusNode,
        selection.focusOffset,
      ],
      selection,
    );
  }

  if (chunks) {
    if (!sanitizationOptions) {
      throw new Error('programmer error: chunks but no sanitization options???');
    }
    if (!chunks.docId) {
      throw new Error('programmer error: chunks but no docId');
    }
    // Must await: initChunkedContent returns a DeferredPromise that resolves in requestAnimationFrame.
    // Without await, updateChunkedContent can be called before initialization completes (race condition).
    // See: https://readwise.sentry.io/issues/6436046479/
    await initChunkedContent(containerNode, chunks, sanitizationOptions, documentUrl);
  }
  initHighlightTrackingForChunking(containerNode);
  eventEmitter.on('chunk-content-loaded', onChunkContentLoaded);
  eventEmitter.on('chunk-content-unloaded', onChunkContentUnloaded);

  selectionEventsHandler = new SelectionEventsHandler({
    onSelectionChange: async () => {
      const selection = window?.getSelection();
      let selectionText = selection?.toString() || '';

      if (selectionText && selection?.rangeCount && renderer && renderer.containerNode !== document.body) {
        const cropped = cropRange(
          selection.getRangeAt(0),
          getHighlightableContentRange(renderer.containerNode),
        );
        selectionText = cropped?.toString() || '';
      }

      portalGateToForeground.emit('selection-change', selectionText);

      if (isInReactNativeWebView && (window as LenientWindow).isAutoHighlightingEnabled) {
        const containerElement = document.getElementById('document-text-content');
        if (containerElement) {
          const lang = document.documentElement.lang || 'unknown';
          const meetsThreshold = selectionText
            ? getWords(selectionText, lang).length >= MINIMUM_WORD_COUNT_TO_AUTO_HIGHLIGHT
            : false;
          containerElement.classList.toggle('document-text-content--would-auto-highlight-selection', meetsThreshold);
        }
      }

      if (selection) {
        if (highlightResizeState.status !== 'inactive') {
          modifySelectionToMaintainHighlightElementEndAsAnchor(selection);
        }

        try {
          modifyOnGoingSelectionToGrabPunctuationOnIOS();
        } catch (e) {
          // We failed to modify the selection, lets not crash and burn
          logger.warn('Failed to modify selection', { error: e });
        }

        if (
          highlightResizeState.status === 'native-selection-made-but-user-hasnt-started-resizing-yet' &&
          initialSelectionDetailsOfHighlightBeingResized &&
          !isEqual(
            initialSelectionDetailsOfHighlightBeingResized,
            pick(selection, ['focusNode', 'focusOffset']),
          )
        ) {
          setHighlightResizeState({
            ...highlightResizeState,
            status: 'actively-resizing',
          });
        }
      }

      const isHighlightableSelectionResult = isHighlightableSelection({ highlightResizeState });
      if (isHighlightableSelectionResult.isValid) {
        return;
      }

      portalGateToForeground.emit('invalid-selection-completed');
      if (isHighlightableSelectionResult.reason !== 'too-short') {
        portalGateToForeground.emit('invalid-manual-selection-completed');
      }
    },
    onTextOrImageSelectedWithMouse: async (finalEvent: MouseEvent) => {
      const isHighlightableSelectionResult = isHighlightableSelection({ highlightResizeState });
      if (!isHighlightableSelectionResult.isValid) {
        portalGateToForeground.emit('invalid-selection-completed');

        if (isHighlightableSelectionResult.reason === 'too-short') {
          const selectionInfoExpandedToHighlightBounds = await getCurrentSelectionInfo({
            shouldExpandToHighlightBounds: true,
          });
          portalGateToForeground.emit('valid-manual-selection-completed', {
            isAltPressed: finalEvent.altKey,
            isTooShortToAutoHighlight: true,
            selectionInfoExpandedToHighlightBounds: selectionInfoExpandedToHighlightBounds ?? undefined,
          });
        } else {
          portalGateToForeground.emit('invalid-manual-selection-completed');
        }
      } else {
        const selectionInfoExpandedToHighlightBounds = await getCurrentSelectionInfo({
          shouldExpandToHighlightBounds: true,
        });
        portalGateToForeground.emit('valid-manual-selection-completed', {
          isAltPressed: finalEvent.altKey,
          isTooShortToAutoHighlight: false,
          selectionInfoExpandedToHighlightBounds: selectionInfoExpandedToHighlightBounds ?? undefined,
        });

        portalGateToForeground.emit('valid-selection-completed', {
          finalEventName: finalEvent.type,
          isAltPressed: finalEvent.altKey,
          selectionInfoExpandedToHighlightBounds,
        });
      }
    },

    /*
      This does not work in the Android web view (well, the underlying touchend event). It's the `contextmenu` event that
      causes a highlight to be created when auto-highlighting is enabled.
    */
    onTextOrImageSelectedWithTouch: async (finalEvent) => {
      const isHighlightableSelectionResult = isHighlightableSelection({ highlightResizeState });
      if (!isHighlightableSelectionResult.isValid) {
        portalGateToForeground.emit('invalid-selection-completed');

        if (isHighlightableSelectionResult.reason === 'too-short') {
          const selectionInfoExpandedToHighlightBounds = await getCurrentSelectionInfo({
            shouldExpandToHighlightBounds: true,
          });
          portalGateToForeground.emit('valid-manual-selection-completed', {
            isTooShortToAutoHighlight: true,
            selectionInfoExpandedToHighlightBounds: selectionInfoExpandedToHighlightBounds ?? undefined,
          });
        } else {
          portalGateToForeground.emit('invalid-manual-selection-completed');
        }
      } else {
        const selectionInfoExpandedToHighlightBounds = await getCurrentSelectionInfo({
          shouldExpandToHighlightBounds: true,
        });
        portalGateToForeground.emit('valid-manual-selection-completed', {
          isTooShortToAutoHighlight: false,
          selectionInfoExpandedToHighlightBounds: selectionInfoExpandedToHighlightBounds ?? undefined,
        });

        portalGateToForeground.emit('valid-selection-completed', {
          finalEventName: finalEvent.type,
          selectionInfoExpandedToHighlightBounds,
        });
      }
    },
  });

  immediateDragTextSelection = initializeImmediateDragTextSelectionHandler();

  // If already initialized after promise is resolved
  if (isInitialized) {
    return;
  }

  const tagsWhichCanBeHighlightedViaDoubleTap = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li', 'blockquote'];

  const onDocDoubleTap = async (target: EventTarget | null) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    if (!isInReactNativeWebView || !target || !isHTMLElement(target as Node)) { // Explanation: target is EventTarget | null; guarded by !target above and isHTMLElement confirms it is a Node
      return;
    }

    const element = closestWith<HTMLElement>(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      getClosestHTMLElement(target as Node), // Explanation: target is EventTarget; the isHTMLElement guard above confirms it is a DOM Node at this point
      (node) => isHTMLElement(node) && tagsWhichCanBeHighlightedViaDoubleTap.includes(node.tagName.toLowerCase()) ||
        checkIsCalibreBlockLevelElement(node),
    );
    if (!element) {
      return;
    }
    portalGateToForeground.emit('element-double-clicked', {
      selectionInfo: await getSelectionInfoFromElements({ elements: [element] }),
    });
  };

  doc.addEventListener('contextmenu', onContextMenuOpened);
  doc.addEventListener('pointercancel', onPointerCancel);
  doc.addEventListener('pointerdown', onPointerDown);
  doc.addEventListener('pointerup', onPointerUp);
  // On mobile we want access to `event.touches`
  doc.addEventListener(isMobile ? 'touchmove' : 'pointermove', onPointerOrTouchMove);
  doc.addEventListener('touchcancel', onTouchCancel);
  doc.addEventListener('dragstart', (e) => {
    const documentContent = doc.getElementById('document-text-content');
    if (documentContent?.classList.contains('drag-prevention-enabled')) {
      e.preventDefault();
    }
  });

  doc.ondblclick = async (event) => {
    onDocDoubleTap(event.target);
  };

  // ondblclick doesn't work with stylus so we need to detect double tap manually
  destroyStylusDoubleTapListeners = detectStylusDoubleTap(doc, onDocDoubleTap);

  isInitialized = true;
  internalContentFrameEventEmitter.emit('initialized');
  if (isInReactNativeWebView && initialPositionData) {
    await mobileInitDocument(layoutMode, initialPositionData);
  }
  logger.debug('Init: done', { docId, containerNodeSelector });

  initializeObservers({ containerNode });
};

export const onHighlightActivated = async (id: Highlight['id']): Promise<void> => {
  logger.debug('onHighlightActivated', { id });
  if (idOfActivatedHighlight) {
    if (id === idOfActivatedHighlight) {
      return;
    }
    removeActiveClassFromAllHighlightElements();
  }
  // eslint-disable-next-line require-atomic-updates
  idOfActivatedHighlight = id;
  onHighlightResizeHandlesVisibilityUpdated();

  for (const highlightElement of getHighlightElementsForId(id)) {
    classListSafe.add(highlightElement, getActiveClassName(highlightElement));
  }
};

export const onHighlightDeactivated = async (): Promise<void> => {
  logger.debug('onHighlightDeactivated', { idOfActivatedHighlight });
  if (!idOfActivatedHighlight) {
    return;
  }

  idOfActivatedHighlight = null;
  onHighlightResizeHandlesVisibilityUpdated();
  removeActiveClassFromAllHighlightElements();
};

export async function onResizeHighlightUserInteractionFinished() {
  logger.debug('onResizeHighlightUserInteractionFinished', { highlightResizeState });
  if (highlightResizeState.status === 'inactive') {
    return;
  }
  setHighlightResizeState({
    ...highlightResizeState,
    status: 'user-interaction-done-waiting-for-render',
  });
}

export const removeHighlights = async (
  highlightIds: Highlight['id'][] | 'all',
  shouldUpdateKnownHighlights = true,
): Promise<void> => {
  logger.debug('removeHighlights', { highlightIds, knownHighlights, shouldUpdateKnownHighlights });
  if (!isInitialized) {
    return;
  }

  const knownHighlightsToKeep: typeof knownHighlights = {};
  let knownHighlightsToRemove: typeof knownHighlights = {};

  if (highlightIds === 'all') {
    knownHighlightsToRemove = knownHighlights;
  } else {
    for (const knownHighlightsHighlight of Object.values(knownHighlights)) {
      if (highlightIds.includes(knownHighlightsHighlight.id)) {
        knownHighlightsToRemove[knownHighlightsHighlight.id] = knownHighlightsHighlight;
      } else {
        knownHighlightsToKeep[knownHighlightsHighlight.id] = knownHighlightsHighlight;
      }
    }
  }

  if (shouldUpdateKnownHighlights) {
    knownHighlights = knownHighlightsToKeep;

    let didHandleVisibilityChange = false;
    if (
      idOfActivatedHighlight &&
      knownHighlightsToRemove[idOfActivatedHighlight] &&
      !isHighlightResizeWaitingForRender(idOfActivatedHighlight)
    ) {
      idOfActivatedHighlight = null;
      didHandleVisibilityChange = true;
    }
    if (
      idOfHighlightHoveredOver &&
      knownHighlightsToRemove[idOfHighlightHoveredOver] &&
      !isHighlightResizeWaitingForRender(idOfHighlightHoveredOver)
    ) {
      idOfHighlightHoveredOver = null;
      didHandleVisibilityChange = true;
    }
    if (didHandleVisibilityChange) {
      onHighlightResizeHandlesVisibilityUpdated();
    }

    if (
      highlightResizeState.idOfHighlightBeingResized &&
      knownHighlightsToRemove[highlightResizeState.idOfHighlightBeingResized] &&
      !isHighlightResizeWaitingForRender(highlightResizeState.idOfHighlightBeingResized)
    ) {
      resetHighlightResizeState();
    }
  }

  const rendererThatExists = getRendererOrThrow();
  let hasWarnedThatContainerNodeIsDetached = false;

  for (const highlightId of Object.keys(knownHighlightsToRemove)) {
    if (knownHighlights[highlightId]?.status === KnownHighlightStatus.RemovalInProgress) {
      logger.warn('Skipping highlight removal, already being removed', knownHighlights[highlightId]);
      continue;
    }

    if (shouldUpdateKnownHighlights) {
      removeTrackedHighlightAfterDelete(highlightId);
    }

    knownHighlightsToRemove[highlightId].intersectionObserver?.disconnect();

    knownHighlightsToRemove[highlightId] = {
      ...knownHighlightsToRemove[highlightId],
      status: KnownHighlightStatus.RemovalInProgress,
      intersectionObserver: null,
    } as RemovalInProgressKnownHighlight;

    const elementsToRemove = getHighlightElementsForId(highlightId);

    const isContainerNodeDetached = !document.body.contains(rendererThatExists.containerNode);
    if (isContainerNodeDetached && !hasWarnedThatContainerNodeIsDetached) {
      logger.warn('renderer containerNode is no longer alive');
      hasWarnedThatContainerNodeIsDetached = true;
    }

    /*
      If it's important that the rest runs even if there are no elements found. I.e. don't exit early.
      This is because the Renderer removes "highlights" from Rangy's internal list. The rest of the code,
      e.g. prepareHighlightElementsForDeletion, will safely run even if an empty array is passed.
    */
    if (
      !isContainerNodeDetached &&
      knownHighlightsToRemove[highlightId].renderedData &&
      !elementsToRemove.length
    ) {
      logger.warn(`removeHighlights: no elements found for highlight#${highlightId}`);
    }

    prepareHighlightElementsForDeletion(elementsToRemove);

    const textElements = elementsToRemove.filter(
      (element) => !(element instanceof HTMLImageElement),
    );
    const removalPromise = waitForElementsToBeRemovedFromDom({
      container: rendererThatExists.containerNode,
      elements: textElements,
    });

    if (knownHighlightsToRemove[highlightId].renderedData) {
      await rendererThatExists.removeHighlight({
        elements: elementsToRemove,
        rangyHighlight: (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          knownHighlightsToRemove[highlightId].renderedData as NonNullable< // Explanation: guarded by if (knownHighlightsToRemove[highlightId].renderedData) above; renderedData is non-null at this point
            KnownHighlight['renderedData']
          >
        ).rangyHighlight,
      });
    }

    await Promise.all([removalPromise, delay(10)]);
  }

  // to be safe
  if (highlightIds === 'all' && rendererThatExists._rangyHighlighter) {
    rendererThatExists._rangyHighlighter.highlights = [];
  }

  foregroundEventEmitter.emit('content-frame:highlights-removed');
};

export async function resetHighlightResizeStateIfWaitingForRender(highlightId: Highlight['id'] | 'any') {
  logger.debug('resetHighlightResizeStateIfWaitingForRender');
  if (isHighlightResizeWaitingForRender(highlightId)) {
    await resetHighlightResizeState();
  }
}

export async function createSerializedPositionRange(startSerializedPosition: string, endSerializedPosition?: string): Promise<{
  range: Range | null;
  startNode: Node;
}> {
  if (!startSerializedPosition) {
    throw new Error('startSerializedPosition is required');
  }

  if (!isInitialized) {
    await internalContentFrameEventEmitter.waitFor('initialized');
  }

  await forceChunkContentLoadAtPosition(startSerializedPosition);

  const renderer = getRendererOrThrow();
  if (!renderer.classApplier) {
    throw new Error('renderer.classApplier missing');
  }
  let range: Range | null = null;

  const { node: startNode, offset: startOffset } = deserializeCanonicalPosition({
    classApplier: renderer.classApplier,
    rootNode: renderer.containerNode,
    serialized: startSerializedPosition,
    tolerance: {
      maxAncestorClimb: 2,
      maxSiblingSpan(ancestorClimb) {
        if (ancestorClimb > 1) {
          return 0;
        }
        if (ancestorClimb) {
          return 3;
        }
        return 5;
      },
    },
  });

  if (!endSerializedPosition) {
    return { range, startNode };
  }

  await forceChunkContentLoadAtPosition(endSerializedPosition);

  const { node: endNode, offset: endOffset } = deserializeCanonicalPosition({
    classApplier: renderer.classApplier,
    rootNode: renderer.containerNode,
    serialized: endSerializedPosition,
    tolerance: {
      maxAncestorClimb: 2,
      maxSiblingSpan(ancestorClimb) {
        if (ancestorClimb > 1) {
          return 0;
        }
        if (ancestorClimb) {
          return 3;
        }
        return 5;
      },
    },
  });

  const startVersusEndPositionComparison = startNode.compareDocumentPosition(endNode);
  if (startVersusEndPositionComparison === Node.DOCUMENT_POSITION_PRECEDING) {
    throw new Error('end is before start');
  }

  const innerRange = new Range();
  innerRange.setStart(startNode, startOffset);
  innerRange.setEnd(endNode, endOffset);
  range = innerRange;

  return { range, startNode };
}

const setTargetToScrollToImpl = async ({
  containerNodeSelector,
  docId: docIdArgument,
  target,
}: {
  containerNodeSelector: string;
  docId: FirstClassDocument['id'];
  target: DocumentScrollTarget | null;
}) => {
  hasScrollTargetRendered = false;
  targetToScrollTo = target;

  if (!targetToScrollTo) {
    hasScrollTargetRendered = true;
    return;
  }

  const containerNode = containerNodeSelector
    ? document.querySelector<HTMLElement>(containerNodeSelector)
    : document.body;

  if (targetToScrollTo.category === 'highlightId') {
    if (!docIdArgument) {
      throw new Error('No docId argument');
    }
    addHighlightIdsToDocIdToIdsOfHighlightThatMustExistMap(docIdArgument, [targetToScrollTo.item]);

    if (!containerNode) {
      throw new Error("Can't find containerNode");
    }

    const knownHighlight = knownHighlights[targetToScrollTo.item];
    if (knownHighlight?.status === KnownHighlightStatus.Rendered) {
      // Try to scroll to it right now
      processHighlights({ contentContainer: containerNode });
    } else if (isInitialized) {
      // This will cause a scroll once it's done
      await renderHighlight({ id: targetToScrollTo.item });
    } // Otherwise it'll render and scroll once we're initialized
    return;
  }

  if (['lexposition', 'lexpositions'].includes(targetToScrollTo.category) && !hasScrollTargetRendered) {
    try {
      const startSerializedPosition = convertLexpositionToSerializedPosition(targetToScrollTo.category === 'lexposition' ? targetToScrollTo.item : targetToScrollTo.item.start);

      // After navigating, the previous doc may still be mounted here (docId === old doc);
      // wait for the target doc so the position resolves against its chunks, not the
      // previous doc's containers, instead of surfacing a false scroll failure.
      if (!isInitialized || docId !== docIdArgument) {
        await internalContentFrameEventEmitter.waitFor('initialized');
      }

      // Load revelant chunk
      await forceChunkContentLoadAtPosition(startSerializedPosition);

      // Check again after async stuff has finished to be safe
      if (!hasScrollTargetRendered) {
        hasScrollTargetRendered = true;

        // Deserialize serialized position
        const { startNode } = await createSerializedPositionRange(startSerializedPosition);

        let startElement = getClosestHTMLElement(startNode);
        if (!startElement) {
          throw new Error("Couldn't get closest HTML from node the (start) serialized position pointed at");
        }

        let highlightRange: Range | undefined;
        if (targetToScrollTo.category === 'lexpositions' && CSS.highlights) {
          if (targetToScrollTo.item.text) {
            try {
              const textToEmphasizeFindResult = await findTextToEmphasize({
                startLexposition: targetToScrollTo.item.start,
                endLexposition: targetToScrollTo.item.end,
                text: targetToScrollTo.item.text,
              });
              if (textToEmphasizeFindResult) {
                startElement = textToEmphasizeFindResult.startElement;
                highlightRange = textToEmphasizeFindResult.range;
              }
            } catch (error) {
              logger.warn('setTargetToScrollTo: errored while finding the textToEmphasize', { error });
            }
          }

          try {
            // If we didn't find the location yet...
            if (!highlightRange) {
              const endSerializedPosition = convertLexpositionToSerializedPosition(targetToScrollTo.item.end);
              const { range } = await createSerializedPositionRange(startSerializedPosition, endSerializedPosition);
              if (range) {
                highlightRange = range;
              }
            }
          } catch (error) {
            logger.warn('setTargetToScrollTo: handling of end lexposition errored', { error });
          }
        }

        await scrollToElementAndEmphasize({
          element: startElement,
          range: highlightRange,
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        logger.error(error.message, { error });
      }
      throw error;
    }
    return;
  }

  throw new Error('Unsupported target category');
};

// Serialize calls so they cannot overlap. The impl mutates module-level
// `targetToScrollTo` / `hasScrollTargetRendered` across multi-second awaits
// (event-emitter initialization, chunk loading), and two concurrent calls
// would clobber each other's state. A sequential queue ensures each call
// fully completes before the next starts, while preserving the awaitable
// Promise<void> contract so callers still receive errors.
let setTargetToScrollToQueue: Promise<unknown> = Promise.resolve();
export const setTargetToScrollTo: typeof setTargetToScrollToImpl = (args) => {
  const result = setTargetToScrollToQueue.then(
    () => setTargetToScrollToImpl(args),
    () => setTargetToScrollToImpl(args),
  );
  setTargetToScrollToQueue = result.catch(() => {});
  return result;
};

function clearScrollTargetEmphasis() {
  clearCssHighlightClass('scroll-target-highlight');
  if (removeRenderedScrollTargetListeners) {
    removeRenderedScrollTargetListeners();
    removeRenderedScrollTargetListeners = null;
  }
}

async function findTextToEmphasize({
  startLexposition,
  endLexposition,
  text,
}: {
  startLexposition: string;
  endLexposition: string;
  text: string;
}): Promise<{
  range: Range;
  startElement: HTMLElement;
} | null> {
  const startSerialized = convertLexpositionToSerializedPosition(startLexposition);
  const endSerialized = convertLexpositionToSerializedPosition(endLexposition);
  const { range: withinRange } = await createSerializedPositionRange(startSerialized, endSerialized);

  if (!withinRange) {
    return null;
  }

  // Use Rangy's findText limited to withinRange with a tolerant regex to handle whitespace/punctuation/quotes
  const withinRangy = convertRangeToRangyRange(withinRange);
  const findRange = rangy.createRange(getRendererOrThrow().containerNode.ownerDocument);

  const escapedText = text
    .trim()
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // collapse multiple spaces/newlines
    .replace(/(\s{2,}|\n)/g, '\\s+')
    // optional whitespace after periods before uppercase
    .replace(/(\.)([A-Z])/g, (match, g1, g2) => `${g1}\\s*?${g2}`)
    // flexible dashes
    .replace(/\\-/g, '[-–—]')
    // flexible spaces around colon/comma
    .replace(/:/g, '\\s*:\\s*')
    .replace(/,/g, '\\s*,\\s*')
    // flexible parentheses spacing
    .replace(/\\\(/g, '\\s*\\(')
    .replace(/\\\)/g, '\\)\\s*')
    // straight vs curly quotes
    .replace(/"/g, '["""]')
    .replace(/'/g, "[''']");
  const tolerantRegex = new RegExp(escapedText, 'gmi');

  const found = findRange.findText(tolerantRegex, { withinRange: withinRangy });
  let range: Range | null = null;
  if (found) {
    range = findRange.nativeRange as Range;
  } else {
    range = withinRange;
  }

  const startElement = getClosestHTMLElement(range.startContainer);
  if (startElement) {
    return {
      range,
      startElement,
    };
  }

  return null;
}

async function scrollToElementAndEmphasize({
  element,
  range,
  emphasizeDelay = 300, // 300ms delay before emphasizing the scroll target
}: {
  element: HTMLElement;
  range?: Range;
  emphasizeDelay?: number;
}) {
  // clear existing scroll target emphasis first to ensure consistent layout
  clearScrollTargetEmphasis();

  if (isInReactNativeWebView) {
    getScrollingManager()._scrollToRangeOrElement(range ?? element);
  } else {
    // 175px clears the doc header; a video doc's sticky player needs more (RW-45974).
    // so we scale the offset based on how big the video element is.
    const player = document.getElementById('yt-player-container');
    const scrollMarginTop = player && !player.closest('#document-text-content')
      ? player.getBoundingClientRect().bottom + 175
      : 175;
    element.style.scrollMarginTop = `${scrollMarginTop}px`;

    element.scrollIntoView({
      block: 'start',
      inline: 'center',
      behavior: 'auto',
    });
  }

  portalGateToForeground.emit('scroll-target-scrolled-to');

  if (range) {
    await delay(emphasizeDelay);
    clearScrollTargetEmphasis();
    highlightRange(range, 'scroll-target-highlight');

    const scrollingElement = document.scrollingElement ?? document.documentElement;
    if (scrollingElement) {
      const initialScrollTop = scrollingElement.scrollTop;

      const handleScroll = () => {
        if (Math.abs(scrollingElement.scrollTop - initialScrollTop) > 100) {
          clearScrollTargetEmphasis();
        }
      };

      const handleClick = () => {
        clearScrollTargetEmphasis();
      };

      document.addEventListener('click', handleClick, { once: true });
      scrollingElement.addEventListener('scroll', handleScroll, { passive: true });

      removeRenderedScrollTargetListeners = () => {
        document.removeEventListener('click', handleClick);
        scrollingElement.removeEventListener('scroll', handleScroll);
      };
    }
  }
}

export const updateIcons = async (
  highlightId: Highlight['id'],
  details: {
    note: boolean;
    tag: boolean;
  },
): Promise<void> => {
  const detailsEntries = Object.entries(details);
  let didUpdate = false;
  for (const element of getHighlightElementsForId(highlightId)) {
    for (const [iconName, shouldExist] of detailsEntries) {
      const shouldActuallyExist = iconName === 'tag' && isDocumentShareApp ? false : shouldExist;
      const className = `${Renderer.highlightClassName}--has-${iconName}`;
      // We wouldn't need to check this, except that we want to call something afterwards if there was an update
      const doesExist = element.classList.contains(className);
      if (doesExist !== shouldActuallyExist) {
        classListSafe[shouldActuallyExist ? 'add' : 'remove'](element, className);
        didUpdate = true;
      }
    }
  }

  if (didUpdate) {
    portalGateToForeground.emit('content-moved');
  }
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export async function scrollToAnchor({ url, anchorText }: AnchorScrollTarget) {
  if (!renderer) {
    logger.warn('Cannot scroll to document link position, document is not open in Reader');
    return;
  }
  const targetUrl = normalizeUrl(url);
  const anchors = Array.from(getRendererOrThrow().containerNode.getElementsByTagName('a'));
  // Find the best matching anchor based on anchorText and the longest URL prefix.
  const targetAnchor = anchors.reduce(
    (bestMatchAnchor, anchor) => {
      if (anchor.innerText.trim().indexOf(anchorText.trim()) === -1) {
        return bestMatchAnchor;
      }

      const anchorUrl = normalizeUrl(anchor.href);
      if (anchorUrl.indexOf(targetUrl) === -1) {
        return bestMatchAnchor;
      }

      // This anchor's URL has a shorter prefix than the current best match, so skip it.
      // We use longest prefix here to avoid perfectly replicating the omission of query params in Python normalize_url().
      if (bestMatchAnchor !== undefined && anchorUrl.length < bestMatchAnchor.href.length) {
        return bestMatchAnchor;
      }

      // Don't bother with prefix matches that are shorter than 20 chars.
      if (anchorUrl.length < 20) {
        return bestMatchAnchor;
      }

      return anchor;
    },
    undefined as HTMLAnchorElement | undefined,
  );
  if (targetAnchor === undefined) {
    const anchorHrefs = anchors.map((a) => a.href);
    const anchorHrefsNormalized = anchorHrefs.map((href) => normalizeUrl(href));
    logger.warn('Could not find target anchor to scroll to', {
      anchors,
      anchorHrefs,
      anchorHrefsNormalized,
      url,
      normalizedUrl: targetUrl,
    });
    return;
  }
  targetAnchor.focus({
    preventScroll: true,
  });
  await delay(50);
  foregroundEventEmitter.emit('refocus-content-focus-indicator');
}

// Returns a short textual context around a highlight by looking at the closest "focusable" ancestor(s)
export const getSurroundingTextForHighlight = async (
  highlightId: Highlight['id'],
): Promise<string | undefined> => {
  if (!isInitialized) {
    throw new Error('Not initialized');
  }

  const known = knownHighlights[highlightId];
  if (!known) {
    return undefined;
  }

  if (
    [
      KnownHighlightStatus.NonRendered,
      KnownHighlightStatus.UnrenderedInUnloadedChunk,
      KnownHighlightStatus.RenderInProgress,
    ].includes(known.status)
  ) {
    try {
      await renderHighlight({ id: highlightId });
    } catch {
      return;
    }
  }

  const highlightElements = getHighlightElementsForId(highlightId);
  if (!highlightElements.length) {
    return undefined;
  }

  return getSurroundingTextFromElements(highlightElements);
};

// Same as getSurroundingTextForHighlight but for the current text selection
export function getSurroundingTextForSelection(): string | undefined {
  if (!isInitialized) {
    return undefined;
  }

  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) {
    return undefined;
  }

  const range = selection.getRangeAt(0);
  if (!range) {
    return undefined;
  }

  const startElements: HTMLElement[] = [];
  for (const node of [range.startContainer, range.endContainer]) {
    const element = node instanceof HTMLElement ? node : node.parentElement;
    if (element) {
      startElements.push(element);
    }
  }

  return startElements.length > 0 ? getSurroundingTextFromElements(startElements) : undefined;
}

export async function getVisibleDocumentContentAsMarkdown(args: Omit<Parameters<typeof getVisibleContentElements>[0], 'containerElement'>): Promise<string> {
  if (!renderer) {
    return '';
  }

  const elements = getVisibleContentElements({
    ...args,
    containerElement: renderer.containerNode,
  });
  const html = elements.map((element) => element.outerHTML).join('');
  return turndownService.turndown(html).trim();
}

export async function scrollSelectionIntoView(
  options: Omit<Parameters<typeof scrollIntoView>[1], 'scrollableAncestor'> & {
    skipIfAlreadyInViewportRange?: { top: number; bottom: number; };
  },
) {
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) {
    return;
  }
  const range = selection.getRangeAt(0);
  if (!range) {
    return;
  }

  const rect = range.getBoundingClientRect();

  if (options.skipIfAlreadyInViewportRange) {
    const viewportHeight = window.innerHeight;
    const rangeTop = viewportHeight * options.skipIfAlreadyInViewportRange.top;
    const rangeBottom = viewportHeight * options.skipIfAlreadyInViewportRange.bottom;
    if (rect.top >= rangeTop && rect.bottom <= rangeBottom) {
      return;
    }
  }

  let scrollableAncestor: HTMLElement;

  if (isInReactNativeWebView) {
    scrollableAncestor = getScrollingManager('scrollSelectionIntoView').getScrollingElement();
  } else {
    const scrollingElement = document.scrollingElement ?? document.documentElement;
    if (!isHTMLElement(scrollingElement)) {
      throw new Error('scrollingElement is not a HTML element');
    }
    scrollableAncestor = scrollingElement;
  }

  return scrollIntoView(rect, {
    ...options,
    scrollableAncestor,
  });
}

export async function mobileOnAndroidTouchEnd() {
  if (!isAndroidQuickLookupPendingFingerRelease) {
    return;
  }

  isAndroidQuickLookupPendingFingerRelease = false;
  if (!renderer) {
    return;
  }

  const isHighlightableSelectionResult = renderer.isHighlightableSelection({
    highlightResizeState,
  });
  if (isHighlightableSelectionResult?.reason !== 'too-short') {
    return;
  }

  const selectionInfoExpandedToHighlightBounds = await getCurrentSelectionInfo({
    shouldExpandToHighlightBounds: true,
    shouldNotGrabPunctuation: true,
  });
  portalGateToForeground.emit('valid-manual-selection-completed', {
    isTooShortToAutoHighlight: true,
    selectionInfoExpandedToHighlightBounds: selectionInfoExpandedToHighlightBounds ?? undefined,
  });
}

// eslint-disable-next-line import/no-cycle
export * from '../mobileContentFramePortalGateMethods';
export * from './chunkedContent';
export * from './findInDocument';
// eslint-disable-next-line import/no-cycle
export * from './tts';
